import {
  DIAGRAM_ARROW_KIND,
  DIAGRAM_ARROW_MAX_POINTS,
  DIAGRAM_GOAL_SIZE,
  DIAGRAM_HURDLE_ROTATION,
  DIAGRAM_TEAM,
  DIAGRAM_MAX_ELEMENTS,
  DIAGRAM_MAX_STEP,
  DIAGRAM_MEASURE_LABEL_MAX,
  DIAGRAM_PITCH,
  DIAGRAM_PITCH_LABELS,
  DIAGRAM_TEXT_MAX,
  DIAGRAM_ZONE_COLOR,
  DIAGRAM_ZONE_COLOR_LABELS,
  DIAGRAM_ZONE_ROTATION,
  DIAGRAM_ZONE_SHAPE,
  DIAGRAM_ZONE_SHAPE_LABELS,
  diagramSteps,
  type DiagramArrowKind,
  type DiagramElement,
  type DiagramHurdleRotation,
  type DiagramPitch,
  type DiagramStep,
  type DiagramSymbolScale,
  type DiagramTeam,
  type DiagramZoneColor,
  type DiagramZoneRotation,
  type DiagramZoneShape,
  type TrainingDiagram,
} from '@ukinfo/shared';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Button, Checkbox, Field, Input, Select, cn, useEscapeToClose, useScrollLock } from '../ui';
import {
  AREA_WIDTH_DEFAULT_METERS,
  DRAG_THRESHOLD,
  HIT_STROKE,
  HIT_ZONE_STROKE,
  arrowMiddle,
  bendArrow,
  clientToField,
  defaultGoalSize,
  fieldDistance,
  formatMeters,
  goalRotationAt,
  gridStepFor,
  HIT_RADIUS,
  hitRadius,
  hitSize,
  measureMeters,
  moveArrowEnd,
  nextPlayerLabel,
  PITCH_WIDTH_METERS,
  straightenArrow,
  zoneFromPoints,
  type FieldPoint,
} from './diagramEdit';
import {
  DEFAULT_ZONE_LOOK,
  FACING_DIRECTIONS,
  FACING_SNAP_DEGREES,
  ZONE_CORNERS,
  ZONE_MIN_SIZE,
  addKnickAtLongest,
  applyZoneLook,
  canAddKnick,
  canSpanSteps,
  extendArrow,
  facingFromPoint,
  insertKnick,
  moveKnick,
  removeKnick,
  resizeZone,
  setFacing,
  setSmooth,
  withFacing,
  withStep,
  withStepEnd,
  zoneCornerPoint,
  zoneLookOf,
  type ZoneCorner,
  type ZoneLook,
} from './diagramElements';
import {
  NO_GUIDES,
  alignElements,
  centerGroupOnField,
  centerOnField,
  centerOnObject,
  changeTeam,
  copyElements,
  distributeElements,
  duplicateOffset,
  elementsInRect,
  isRowSource,
  moveGroup,
  normalizeSelection,
  planRow,
  snapMeasurePoint,
  snapTolerance,
  translateGroup,
  type AlignMode,
  type RowInput,
  type SnapGuides,
} from './diagramGroup';
import { blockFromSelection, type BlockContent } from './diagramBlocks';
import { DRAWN_STRAIGHT, freehandArrow } from './diagramFreehand';
import { reorderSelection, type OrderDirection } from './diagramOrder';
import { stackAt, stackNames } from './diagramStack';
import { OverlapList } from './DiagramOverlapList';
import { transformElements, type TransformOp } from './diagramTransform';
import { drawingBox, isZoomed, pixelsPerUnit } from './diagramView';
import { DiagramBlockPicker, SaveBlockDialog } from './DiagramBlockPicker';
import { DiagramGrid, gridWidthText } from './DiagramGrid';
import { DiagramPlayer } from './DiagramPlayer';
import { TransformPanel } from './DiagramTransformPanel';
import { FormationPicker } from './FormationPicker';
import { EmptySelection, PanelGroup, RowPanel, SelectionPanel, canBeSmall, selectionTitle, type CenterMode, type CenterScope } from './DiagramSelectionBar';
import { ExpandButton, ExpandedShell, HeaderButton, fitBox, useBoxSize, useWideScreen } from './DiagramExpanded';
import { DiagramHelp } from './DiagramHelp';
import { ToolButton, ToolPanel, toolDef as toolDefOf, type ToolId } from './DiagramTools';
import { ViewBar } from './DiagramViewBar';
import {
  ARROW,
  ARROW_LABELS,
  LAYER,
  PITCH_HEIGHT,
  PitchSurface,
  SketchElement,
  StepNumbers,
  TEAM_STYLE,
  ZoneOutline,
  FACING_LABEL,
  arrowGuidePath,
  describeDiagram,
  drawingGeometry,
  elementScale,
  textLayout,
  useMarkerId,
  zoneColorsOf,
} from './PitchDiagram';
import { ZoomControls, useDiagramViewport, type PanGrab } from './useDiagramViewport';

/**
 * Editor fuer die Skizze einer Uebung.
 *
 * Aufbau von oben nach unten: Werkzeuge in benannten Gruppen (`DiagramTools`), darunter die feste Buehne mit Rueckgaengig/Wiederherstellen und
 * "Bedienung" ueber dem Feld und dem aktiven Werkzeug samt Hinweiszeile unter ihm (die eine Stelle fuer alles, was ein Tipp oder Ziehen
 * gerade bewirkt), dann "Ansicht" (`DiagramViewBar`: Zoom, Einrasten, Raster, Schritte, Feld, Symbole, Ablauf ansehen) und zuletzt der Bereich
 * "Auswahl" (`DiagramSelectionBar`: Eigenschaften, Anordnen, Bearbeiten - ohne Auswahl die Befehle fuer die ganze Skizze).
 *
 * Bedienung ueber Pointer Events - Maus, Finger und Stift laufen durch
 * denselben Code. Werkzeug waehlen, dann aufs Feld tippen (Spieler, Huetchen,
 * Ball, Tor, Ausstattung) oder ziehen (Pfeile, Zone). Im Auswahlmodus markiert ein
 * Tipp ein Element, Ziehen verschiebt es. Ein markierter Pfeil hat Griffe an Anfang
 * und Ende und einen in der Mitte, mit dem er sich biegen laesst.
 *
 * Text (Gruppe "Flaechen & Beschriftung"): Tippen setzt ihn und oeffnet sofort das Eingabefeld in der Auswahl unter dem Feld;
 * Doppeltipp auf einen vorhandenen Text bearbeitet ihn. Mit zwei Tipps gezeichnete Pfeile (Anfang, Ende) wachsen
 * mit weiteren Tipps um Knickpunkte, bis "Fertig" oder ein Tipp auf den letzten Punkt sie beendet - ausser Pass, Schuss und hoher Ball
 * (`DRAWN_STRAIGHT`): sie bleiben beim Zeichnen immer gerade (Ziehen = gerader Pfeil, auch ein krumm gezogener; ein weiterer Tipp beginnt
 * einen neuen Pfeil), Knicke setzt man dort nur ausdruecklich ("Knick hinzufuegen", Doppeltipp auf die Linie). Ein markierter Pfeil
 * hat Griffe an allen Knickpunkten (ziehen; Doppeltipp entfernt einen), "Knick hinzufuegen" und -- als Doppeltipp --
 * ein Tipp auf die Linie setzen neue. Ein markierter Spieler hat einen Griff zum Drehen der Blickrichtung (rastet
 * mit "Einrasten" auf 45 Grad), eine markierte Zone Eckgriffe zum Skalieren; Form und Farbe der Zonen stehen unter dem Feld.
 *
 * Weiche Pfeile (`smooth`): Knickpfeile als glatte Kurve durch alle Punkte - Umschalter "Weich/Eckig" in den Eigenschaften; Dribbling und Laufweg
 * werden mit mehreren getippten Punkten weich, Pass, Schuss und Wechsel eckig. Freihand: Wer beim Pfeilwerkzeug (ausser Pass, Schuss, hoher Ball) eine Kurve zieht (mehr als 4 Einheiten
 * neben der Geraden), bekommt einen weichen Pfeil mit den vereinfachten Punkten des Wegs (`diagramFreehand.ts`); fast gerades Ziehen bleibt ein gerader Pfeil.
 * Ein Pass hat in den Eigenschaften "Hinwechseln" (`follow`): der Passgeber laeuft dem Pass nach (Begleitlinie im Bild, Nachlaufen im Ablauf).
 * Bei einer Auswahl steht das Feld am Handy oben fest (`diagram-stage`), damit man sieht, was ein Knopf im Bereich "Auswahl" bewirkt.
 *
 * Die Masslinie ("Mass", Gruppe "Flaechen & Beschriftung") wird wie ein Pfeil gezogen oder mit zwei Tipps gesetzt; ihre Enden
 * rasten (bei eingeschaltetem "Einrasten") an Elementen ein oder fluchten waagrecht/senkrecht zum anderen
 * Ende. Der Positionswechsel ("Wechsel") ist eine Pfeilart wie die anderen.
 *
 * Zentrieren (im Menue "Ausrichten" unter "Anordnen", auch fuer ein einzelnes Element): waagrecht oder senkrecht, am Feld
 * (bei mehreren Elementen zwei Paare: "Gruppe zentrieren" rückt die Auswahl als Ganzes in die Feldmitte, Form und
 * Abstände bleiben; "Auf Mittellinie" rückt jedes Element einzeln auf die Mittellinie des Felds) oder an einem
 * Bezugsobjekt (zuletzt angetipptes Element der Auswahl, dick umrandet; bei einem einzelnen Element tippt man danach
 * ein anderes an). Das Bezugsobjekt bleibt stehen.
 *
 * Mehrfachauswahl: Auf leerer Flaeche zieht man einen Rahmen auf; gezoomt schwenkt dort ein
 * Zeiger weiter, den Rahmen gibt es dann mit dem Umschalter "Mehrfach" (am Desktop mit
 * gedrueckter Umschalttaste). Bei eingeschaltetem Umschalter (oder mit Umschalt-/Strg-Klick)
 * fuegt ein Tipp ein Element zur Auswahl hinzu oder nimmt es heraus. Die Gruppe laesst sich
 * gemeinsam verschieben (mit Einrasten an anderen Elementen und der Feldmitte, abschaltbar),
 * loeschen, duplizieren, ausrichten und verteilen; aus einem einzelnen Punkt-Element entsteht
 * eine Reihe. Die Rechnung dazu steht in `diagramGroup.ts`.
 *
 * Aufbau einfuegen (Gruppe "Einfuegen"): fertige Aufbauten und eigene Bausteine des Vereins kommen in die Feldmitte beziehungsweise an die
 * zuletzt angetippte Stelle und sind danach markiert (`DiagramBlockPicker`); eine Auswahl laesst sich als Baustein speichern.
 * Spiegeln/Drehen (Knopf unter "Anordnen", bei nichts Markiertem unter "Ganze Skizze"): Auswahl oder ganze Skizze, siehe `diagramTransform.ts`.
 * Wiederherstellen ist das Gegenstueck zu Rueckgaengig (Knopf, Strg+Y, Strg+Umschalt+Z); jede neue Aenderung leert es.
 *
 * Raster (Schalter neben "Einrasten" im Bereich "Ansicht"): blendet Linien ein und rastet beim Setzen und Ziehen auf 1 Einheit ein, gezoomt ab 3x auf eine
 * halbe. Das Einrasten an Objekten ("Einrasten") hat Vorrang; beide Schalter merkt sich das Geraet.
 *
 * Laeuft bis Schritt (`stepEnd`, Eigenschaft von Laufweg, Dribbling und Wechsel neben "Schritt"): der Spieler laeuft ohne Halt ueber mehrere Schritte; das Badge zeigt "1–2".
 * Schritte (Schalter im Bereich "Ansicht"): blendet die Schrittnummern an den Pfeilen im Editor aus (je Geraet gemerkt, Voreinstellung an) - reine Ansicht:
 * nichts wird gespeichert, `onChange` und Rueckgaengig bleiben unberuehrt; Eigenschaft "Schritt", "Ablauf ansehen", Detail, Player und Bildexport zeigen sie wie immer.
 *
 * Der Editor liefert ueber `onChange` immer eine Skizze, die das Schema besteht:
 * Koordinaten sind Vielfache von 0,5 im Feld, mehr als 80 Elemente nimmt er nicht an.
 */

/** Vorschlaege fuer die Beschriftung einer Masslinie. */
const MEASURE_SUGGESTIONS = ['5 m', '10 m', '15 m', '20 m', '30 m'] as const;

const GOAL_SIZE_LABELS = { MINI: 'Minitor', YOUTH: 'Jugendtor', FULL: 'Großtor' } as const;
const TEAM_OPTIONS = DIAGRAM_TEAM.map((team) => ({ value: team, label: TEAM_STYLE[team].name }));

/** So viele Schritte lassen sich zuruecknehmen (und wiederherstellen). */
const HISTORY_LIMIT = 50;

/** Schluessel im localStorage: "Einrasten", "Raster" und "Schritte" gelten je Geraet. */
export const SNAP_KEY = 'ukinfo.skizze.einrasten';
export const GRID_KEY = 'ukinfo.skizze.raster';
export const STEPS_KEY = 'ukinfo.skizze.schritte';

function readFlag(key: string, fallback: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(key);
    return stored === '1' ? true : stored === '0' ? false : fallback;
  } catch {
    // Ohne Speicher (privater Modus, gesperrt) gilt die Voreinstellung.
    return fallback;
  }
}

function saveFlag(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Nicht merken ist kein Fehler.
  }
}
/** Ein Zug unter dieser Strecke (Bildschirmpixel) beim Schwenken gilt noch als Tippen. */
const PAN_THRESHOLD = 6;
const SELECT_COLOR = '#38bdf8';
/** Zwei Tipps auf den Biegegriff innerhalb dieser Zeit machen den Pfeil wieder gerade; ebenso Doppeltipps auf Knick, Linie und Text. */
const DOUBLE_TAP_MS = 400;
/** Zwei Tipps auf Linie oder Text gelten nur als Doppeltipp, wenn sie so nah beieinander liegen (Zeichnungseinheiten). */
const DOUBLE_TAP_DISTANCE = 6;
const NOTHING_TO_CENTER = 'Die markierten Elemente sind hier schon zentriert – es ändert sich nichts.';
const NOTHING_TO_CENTER_GROUP = 'Die Gruppe steht hier schon in der Feldmitte – es ändert sich nichts.';
const LIMIT_NOTICE = `Die Skizze hat schon ${DIAGRAM_MAX_ELEMENTS} Elemente – mehr sind nicht möglich. Lösche ein Element, um Platz zu schaffen.`;

/** Ein Zug unter dieser Strecke (Bildschirmpixel) gilt beim Verschieben noch als Tippen: nichts bewegt sich, nichts rastet ein. */
const MOVE_THRESHOLD = 6;
const NO_SELECTION: number[] = [];

/** Ein gerade gesetztes Element: was zu tun ist, damit ein Abbruch es spurlos entfernt. */
interface Placement {
  before: DiagramElement[];
  selectedBefore: number[];
  historyBefore: number;
  /** Der Wiederherstellen-Verlauf vor dem Setzen: ein Abbruch stellt ihn wieder her. */
  futureBefore: Snapshot[];
}

type Drag =
  | {
      mode: 'move';
      pointerId: number;
      /** Alle gemeinsam bewegten Elemente (bei einem einzelnen Element genau eins). */
      indices: number[];
      start: FieldPoint;
      /** Bildschirmposition des Drucks: unter `MOVE_THRESHOLD` Pixeln bewegt sich nichts. */
      screen: { x: number; y: number };
      moved: boolean;
      base: DiagramElement[];
      key: string | null;
      placement: Placement | null;
      /** Was ein reiner Tipp (ohne Ziehen) bewirkt: Element aus der Auswahl nehmen, oder die Auswahl auf dieses Element verengen. */
      tap: { kind: 'remove' | 'narrow' | 'reference' | 'cycle'; index: number } | null;
    }
  /**
   * `end`: 'from', 'to', 'via' (Biegung), 'knick-<i>' (Knickpunkt), 'facing' (Blickrichtung) oder 'corner-<nw|ne|sw|se>' (Zone).
   * `cycle`: das naechste Element des Stapels unter der Stelle - ein Tipp auf den Griff (ohne Ziehen) waehlt es.
   */
  | { mode: 'handle'; pointerId: number; index: number; end: string; base: DiagramElement[]; cycle?: number }
  /** `path`: der gezogene Weg (Feldpunkte) - bei Pfeilen die Grundlage fuer den Freihand-Pfeil. */
  | { mode: 'draw'; pointerId: number; start: FieldPoint; tool: ToolId; anchor: FieldPoint | null; path: FieldPoint[] }
  /** Ein weiterer Tipp verlaengert den eben gezeichneten Pfeil um einen Knickpunkt. */
  | { mode: 'extend'; pointerId: number; index: number }
  | { mode: 'pan'; pointerId: number; grab: PanGrab; startX: number; startY: number; moved: boolean }
  /** Rahmen aufziehen; `base` ist die Auswahl, zu der die Treffer hinzukommen (leer ohne Mehrfachauswahl). */
  | { mode: 'marquee'; pointerId: number; start: FieldPoint; base: number[] }
  /** Zielpunkt einer Reihe "bis zu einem Punkt" setzen. */
  | { mode: 'target'; pointerId: number };

interface Marquee {
  start: FieldPoint;
  current: FieldPoint;
  base: number[];
}

/** Zustand des Bereichs "Reihe erzeugen". */
interface RowState extends RowInput {
  open: boolean;
}

/** Stand fuer Rueckgaengig: die Elemente und die Symbolgroesse der Skizze. */
interface Snapshot {
  elements: DiagramElement[];
  symbolScale: DiagramSymbolScale | undefined;
}

/** Die Elemente unter der zuletzt angetippten Stelle (mehr als eins): `base` ist der Stand der Elemente, auf den sich die Indizes beziehen. */
interface Pile {
  indices: number[];
  base: DiagramElement[];
}

interface Draft {
  start: FieldPoint;
  current: FieldPoint;
  /** Der bisher gezogene Weg eines Pfeils (mit Anfang, ohne den aktuellen Punkt): zeigt schon beim Ziehen, ob es ein Freihand-Pfeil wird. */
  path?: FieldPoint[];
}

/** Der gezogene Weg bis zum Endpunkt (der letzte Punkt wird nicht doppelt gesetzt). */
function pathTo(path: readonly FieldPoint[], end: FieldPoint): FieldPoint[] {
  const last = path[path.length - 1];
  return last && last.x === end.x && last.y === end.y ? [...path] : [...path, end];
}

function arrowKindOf(tool: ToolId): DiagramArrowKind | null {
  return tool.startsWith('arrow') ? (tool.slice(5) as DiagramArrowKind) : null;
}

/* ----------------------------------------------------------- Zeichenflaeche */

/**
 * Unsichtbare, vergroesserte Trefferflaeche - ein Finger trifft nicht auf den Pixel genau.
 * Auf dem Bildschirm bleibt sie etwa gleich gross, egal wie stark gezoomt ist (in
 * Zeichnungseinheiten durch den Zoom geteilt), und unabhaengig von der Symbolgroesse.
 */
function HitShape({ element, height, zoom, scale }: { element: DiagramElement; height: number; zoom: number; scale: number }): JSX.Element | null {
  switch (element.type) {
    case 'zone':
      // Der Umriss der Form (Kreis, Dreieck): was ausserhalb der Form im Rechteck liegt, trifft das Element darunter.
      return <ZoneOutline zone={element} height={height} fill="transparent" stroke="transparent" strokeWidth={hitSize(HIT_ZONE_STROKE, zoom)} />;
    case 'arrow':
    case 'measure':
      return <path d={arrowGuidePath(drawingGeometry(element, height))} stroke="transparent" strokeWidth={hitSize(HIT_STROKE, zoom)} strokeLinecap="round" fill="none" />;
    case 'text': {
      const box = textLayout(element, height, scale);
      const pad = hitSize(1.6, zoom);
      return <rect data-hit="text" x={box.cx - box.w / 2 - pad} y={box.cy - box.h / 2 - pad} width={box.w + 2 * pad} height={box.h + 2 * pad} rx={1} fill="transparent" />;
    }
    default:
      return <circle data-hit={element.type} cx={element.x} cy={(element.y / 100) * height} r={hitRadius(element, zoom)} fill="transparent" />;
  }
}

/** Abstand des Drehgriffs der Blickrichtung vom Mittelpunkt des Spielers in Zeichnungseinheiten (bei Symbolgroesse 1). */
const FACING_GRIP_DISTANCE = 8.6;

/**
 * Markierung des gewaehlten Elements; bei Pfeilen mit Griffen fuer Anfang, Ende und die Biegung
 * in der Mitte (bei Knickpfeilen an jedem Knickpunkt statt der Biegung), bei Zonen mit Eckgriffen, bei Spielern mit
 * einem Drehgriff fuer die Blickrichtung. Griffe und ihre Trefferflaechen bleiben auf dem Bildschirm gleich gross;
 * der Ring um ein Symbol folgt dessen Groesse.
 */
function SelectionMark({
  element,
  height,
  zoom,
  scale,
  group = false,
  activeKnick = null,
}: {
  element: DiagramElement;
  height: number;
  zoom: number;
  scale: number;
  /** Teil einer Mehrfachauswahl: staerker hervorgehoben, ohne Griffe. */
  group?: boolean;
  /** Der zuletzt angefasste Knickpunkt eines markierten Knickpfeils (hervorgehoben). */
  activeKnick?: number | null;
}): JSX.Element {
  const ring = group
    ? ({ fill: SELECT_COLOR, fillOpacity: 0.22, stroke: SELECT_COLOR, strokeWidth: 1.1, pointerEvents: 'none' } as const)
    : ({ fill: 'none', stroke: SELECT_COLOR, strokeWidth: 0.8, strokeDasharray: '2 1.4', pointerEvents: 'none' } as const);
  const grip = hitSize(2.2, zoom);
  const rim = hitSize(0.9, zoom);
  const reach = hitSize(HIT_RADIUS, zoom);
  switch (element.type) {
    case 'zone': {
      const box = <rect data-selection="zone" x={element.x} y={(element.y / 100) * height} width={element.w} height={(element.h / 100) * height} {...ring} />;
      if (group) return box;
      return (
        <g>
          {box}
          {/* Eckgriffe: gefuellte Quadrate, damit sie sich von den runden Griffen der Pfeile unterscheiden. */}
          {ZONE_CORNERS.map((corner) => {
            const at = zoneCornerPoint(element, corner);
            const cy = (at.y / 100) * height;
            return (
              <g key={corner} data-handle={`corner-${corner}`}>
                <circle cx={at.x} cy={cy} r={reach} fill="transparent" />
                <rect x={at.x - grip} y={cy - grip} width={2 * grip} height={2 * grip} fill="#ffffff" stroke={SELECT_COLOR} strokeWidth={rim} pointerEvents="none" />
              </g>
            );
          })}
        </g>
      );
    }
    case 'text': {
      const box = textLayout(element, height, scale);
      const pad = hitSize(0.8, zoom);
      return <rect data-selection="text" x={box.cx - box.w / 2 - pad} y={box.cy - box.h / 2 - pad} width={box.w + 2 * pad} height={box.h + 2 * pad} rx={1} {...ring} />;
    }
    case 'arrow':
    case 'measure': {
      if (group) {
        return (
          <path
            data-selection={element.type}
            d={arrowGuidePath(drawingGeometry(element, height))}
            fill="none"
            stroke={SELECT_COLOR}
            strokeWidth={hitSize(4.2, zoom)}
            strokeLinecap="round"
            opacity={0.55}
            pointerEvents="none"
          />
        );
      }
      const ends = [
        { name: 'from', x: element.from[0], y: (element.from[1] / 100) * height },
        { name: 'to', x: element.to[0], y: (element.to[1] / 100) * height },
      ] as const;
      const knicks = element.type === 'arrow' ? (element.points ?? []) : [];
      // Knickpfeile haben keine Biegung: statt des Griffs in der Mitte sitzt an jedem Knick ein Griff.
      const middle = element.type === 'arrow' && knicks.length === 0 ? arrowMiddle(element) : null;
      return (
        <g data-selection={element.type}>
          <path d={arrowGuidePath(drawingGeometry(element, height))} fill="none" stroke={SELECT_COLOR} strokeWidth={hitSize(3.4, zoom)} strokeLinecap="round" opacity={0.3} pointerEvents="none" />
          {/* Biegegriff in der Mitte: gefuellt, damit er sich von den Griffen an den Enden unterscheidet. Liegt darunter, damit bei kurzen Pfeilen die Enden gewinnen. */}
          {element.type === 'arrow' && middle && (
            <g data-handle="via" data-curved={element.via ? 'true' : undefined}>
              <circle cx={middle.x} cy={(middle.y / 100) * height} r={reach} fill="transparent" />
              <circle cx={middle.x} cy={(middle.y / 100) * height} r={grip} fill={SELECT_COLOR} stroke="#ffffff" strokeWidth={rim} pointerEvents="none" />
            </g>
          )}
          {/* Knickpunkte: kleinere Griffe, der zuletzt angefasste ist gefuellt. */}
          {knicks.map((knick, index) => (
            <g key={`knick-${index}`} data-handle={`knick-${index}`} data-active={activeKnick === index ? 'true' : undefined}>
              <circle cx={knick[0]} cy={(knick[1] / 100) * height} r={reach} fill="transparent" />
              <circle
                cx={knick[0]}
                cy={(knick[1] / 100) * height}
                r={grip * 0.85}
                fill={activeKnick === index ? SELECT_COLOR : '#ffffff'}
                stroke={SELECT_COLOR}
                strokeWidth={rim}
                pointerEvents="none"
              />
            </g>
          ))}
          {ends.map((end) => (
            <g key={end.name} data-handle={end.name}>
              <circle cx={end.x} cy={end.y} r={reach} fill="transparent" />
              <circle cx={end.x} cy={end.y} r={grip} fill="#ffffff" stroke={SELECT_COLOR} strokeWidth={rim} pointerEvents="none" />
            </g>
          ))}
        </g>
      );
    }
    case 'player': {
      const cx = element.x;
      const cy = (element.y / 100) * height;
      const factor = elementScale(element, scale);
      const circle = <circle data-selection="player" cx={cx} cy={cy} r={6 * factor} {...ring} />;
      if (group) return circle;
      // Drehgriff der Blickrichtung: sitzt in dieser Richtung ausserhalb des Rings; ohne Blickrichtung oben, nur als Umriss.
      const angle = ((element.facing ?? 0) * Math.PI) / 180;
      const distance = Math.max(FACING_GRIP_DISTANCE * factor, 6 * factor + grip + rim);
      const gx = cx + Math.sin(angle) * distance;
      const gy = cy - Math.cos(angle) * distance;
      return (
        <g>
          {circle}
          <g data-handle="facing" data-facing={element.facing}>
            <circle cx={gx} cy={gy} r={reach} fill="transparent" />
            <line x1={cx} y1={cy} x2={gx} y2={gy} stroke={SELECT_COLOR} strokeWidth={rim} strokeDasharray={element.facing === undefined ? '1 1.2' : undefined} opacity={0.6} pointerEvents="none" />
            <circle cx={gx} cy={gy} r={grip} fill={element.facing === undefined ? '#ffffff' : SELECT_COLOR} stroke={element.facing === undefined ? SELECT_COLOR : '#ffffff'} strokeWidth={rim} pointerEvents="none" />
          </g>
        </g>
      );
    }
    default:
      return (
        <circle
          data-selection={element.type}
          cx={element.x}
          cy={(element.y / 100) * height}
          r={(element.type === 'goal' || element.type === 'hurdle' ? 7 : 6) * elementScale(element, scale)}
          {...ring}
        />
      );
  }
}

/**
 * Bezugsobjekt beim Zentrieren "An Objekt": dicker, durchgezogener Rahmen in Bernstein - deutlich
 * kraeftiger als die gestrichelte Markierung des gewaehlten Elements.
 */
function ReferenceMark({ element, height, zoom, scale }: { element: DiagramElement; height: number; zoom: number; scale: number }): JSX.Element {
  const color = '#f59e0b';
  const common = { 'data-testid': 'align-reference', pointerEvents: 'none' } as const;
  switch (element.type) {
    case 'zone':
      return (
        <rect {...common} x={element.x} y={(element.y / 100) * height} width={element.w} height={(element.h / 100) * height} fill="none" stroke={color} strokeWidth={hitSize(2, zoom)} />
      );
    case 'text': {
      const box = textLayout(element, height, scale);
      return <rect {...common} x={box.cx - box.w / 2} y={box.cy - box.h / 2} width={box.w} height={box.h} rx={1} fill="none" stroke={color} strokeWidth={hitSize(2, zoom)} />;
    }
    case 'arrow':
    case 'measure':
      return (
        <path
          {...common}
          d={arrowGuidePath(drawingGeometry(element, height))}
          fill="none"
          stroke={color}
          strokeWidth={hitSize(4.4, zoom)}
          strokeLinecap="round"
          opacity={0.6}
        />
      );
    default:
      return (
        <circle
          {...common}
          cx={element.x}
          cy={(element.y / 100) * height}
          r={(element.type === 'goal' || element.type === 'hurdle' ? 7 : 6) * elementScale(element, scale)}
          fill="none"
          stroke={color}
          strokeWidth={hitSize(2, zoom)}
        />
      );
  }
}

/* ------------------------------------------------------------------- Editor */

export function DiagramEditor({
  value,
  onChange,
  disabled,
  flush,
}: {
  value: TrainingDiagram | null;
  onChange: (value: TrainingDiagram | null) => void;
  disabled?: boolean;
  /** Der Editor sitzt am Handy ohne Rahmen bis an den Rand (`DiagramField`): er hält den seitlichen Abstand dann selbst, damit nichts am Bildrand klebt und die klebende Bühne ihn genau ausfüllt. */
  flush?: boolean;
}): JSX.Element {
  const [newPitch, setNewPitch] = useState<DiagramPitch>('AREA');
  const [tool, setTool] = useState<ToolId>('select');
  const [selectionState, setSelectionState] = useState<number[]>(NO_SELECTION);
  const [multi, setMulti] = useState(false);
  const [snap, setSnap] = useState(() => readFlag(SNAP_KEY, true));
  const [grid, setGrid] = useState(() => readFlag(GRID_KEY, false));
  /** Schrittnummern an den Pfeilen im Editor zeigen (Voreinstellung an); reine Ansicht, nichts davon landet in der Skizze. */
  const [showSteps, setShowSteps] = useState(() => readFlag(STEPS_KEY, true));
  const [marquee, setMarquee] = useState<Marquee | null>(null);
  const [guides, setGuidesState] = useState<SnapGuides>(NO_GUIDES);
  const [row, setRow] = useState<RowState>({ open: false, count: '5', direction: 'horizontal', spacing: '10', target: null });
  const [preview, setPreviewState] = useState<DiagramElement[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [anchor, setAnchor] = useState<FieldPoint | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [undoDepth, setUndoDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [formationOpen, setFormationOpen] = useState(false);
  /** Dialog "Aufbau einfuegen", Gruppe "Spiegeln/Drehen" und der Baustein, der gerade gespeichert werden soll. */
  const [insertOpen, setInsertOpen] = useState(false);
  const [transformOpen, setTransformOpen] = useState(false);
  const [blockDraft, setBlockDraft] = useState<BlockContent | null>(null);
  /** Menue "Ausrichten": offen oder nicht, Bezug des Zentrierens und das Bezugsobjekt (siehe `referenceIndex`). */
  const [alignOpen, setAlignOpen] = useState(false);
  const [centerMode, setCenterMode] = useState<CenterMode>('field');
  const [reference, setReference] = useState<number | null>(null);
  /** Form, Farbe und Drehung neuer Zonen (Werkzeug "Zone"). */
  const [zoneLook, setZoneLook] = useState<ZoneLook>(DEFAULT_ZONE_LOOK);
  /** Ein mit zwei Tipps gezeichneter Pfeil (Index), der mit weiteren Tipps um Knickpunkte waechst, bis "Fertig". */
  const [extend, setExtend] = useState<number | null>(null);
  /** Zuletzt angefasster Knickpunkt des markierten Pfeils - ihn entfernt "Knick entfernen" (sonst den letzten). */
  const [activeKnick, setActiveKnick] = useState<number | null>(null);
  /** Zaehler: jede Aenderung bittet das Textfeld der Eigenschaften, den Fokus zu nehmen (neuer Text, Doppeltipp auf Text). */
  const [focusText, setFocusText] = useState(0);
  /** Breite der freien Flaeche in Metern - nur fuer den Vorschlag "Massstab" bei Masslinien, wird nicht gespeichert. */
  const [areaWidth, setAreaWidth] = useState(String(AREA_WIDTH_DEFAULT_METERS));
  /** Was unter der zuletzt angetippten Stelle liegt, wenn es mehr als ein Element ist (Liste "Hier liegen" und Hinweis "Nochmal tippen"). */
  const [pile, setPile] = useState<Pile | null>(null);
  /** Vollbildmodus "Gross bearbeiten": derselbe Editor (gleicher Zustand), nur anders angeordnet - siehe `DiagramExpanded.tsx`. */
  const [expanded, setExpanded] = useState(false);
  /** Im Vollbild: das Popover unter der Kopfzeile zeigt "Ansicht" (oder, bei `helpOpen`, die Bedienung); am Handy ist das Blatt mit der Auswahl offen. */
  const [viewOpen, setViewOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const wide = useWideScreen();
  const [stageSize, stageRef] = useBoxSize();
  const expandRef = useRef<HTMLButtonElement | null>(null);
  const sheetId = useId();

  const surfaceRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  /** Bricht die laufende Ein-Finger-Aktion ab; wird beim zweiten Finger gerufen (Zwei-Finger-Geste). */
  const cancelRef = useRef<() => void>(() => undefined);
  const viewport = useDiagramViewport({ pitch: value?.pitch ?? newPitch, onGestureStart: () => cancelRef.current() });
  const svgRef = viewport.element;
  const fitView = viewport.fit;
  /** Letzter Tipp auf den Biegegriff - zwei kurz hintereinander machen den Pfeil gerade. */
  const viaTap = useRef<{ index: number; time: number } | null>(null);
  /** Letzter Doppeltipp-Kandidat auf einem Knickpunkt (Entfernen). */
  const knickTap = useRef<{ index: number; knick: number; time: number } | null>(null);
  /** Letzter Tipp auf Linie oder Text eines Elements: ein zweiter kurz danach setzt einen Knick beziehungsweise bearbeitet den Text. */
  const bodyTap = useRef<{ index: number; time: number; point: FieldPoint } | null>(null);
  /** Stelle des ersten Tipps auf einen Stapel: weitere Tipps nahe daran (ohne Zeitlimit) waehlen der Reihe nach die Elemente darunter. */
  const stackTap = useRef<FieldPoint | null>(null);
  /** Spiegel von `preview`: der letzte Zug kann noch nicht gezeichnet sein, wenn der Zeiger losgelassen wird. */
  const previewRef = useRef<DiagramElement[] | null>(null);
  const history = useRef<Snapshot[]>([]);
  /** Zurueckgenommene Staende, die "Wiederherstellen" zurueckholt; jede neue Aenderung leert sie. */
  const future = useRef<Snapshot[]>([]);
  /** Zuletzt angetippte Stelle im Feld: dort landet ein eingefuegter Aufbau (ohne sie in der Feldmitte). */
  const lastTap = useRef<FieldPoint | null>(null);
  /** Aufeinanderfolgende Aenderungen mit gleichem Schluessel (Tippen einer Beschriftung) sind ein Schritt. */
  const lastKey = useRef<string | null>(null);
  const valueRef = useRef(value);
  const elementsRef = useRef<DiagramElement[]>(value?.elements ?? []);
  const scaleRef = useRef<DiagramSymbolScale | undefined>(value?.symbolScale);
  valueRef.current = value;
  elementsRef.current = value?.elements ?? [];
  scaleRef.current = value?.symbolScale;

  const markerId = useMarkerId();
  const hintId = useId();
  const helpId = useId();
  const rowPanelId = useId();
  const transformId = useId();
  const guidesRef = useRef<SnapGuides>(NO_GUIDES);

  const setPreview = (next: DiagramElement[] | null): void => {
    previewRef.current = next;
    setPreviewState(next);
  };

  const setSelection = (next: number[]): void => setSelectionState(next.length === 0 ? NO_SELECTION : next);

  /** Hilfslinien nur dann neu zeichnen, wenn sie sich geaendert haben (der Zeiger meldet sich oft). */
  const setGuides = (next: SnapGuides): void => {
    if (JSON.stringify(next) === JSON.stringify(guidesRef.current)) return;
    guidesRef.current = next;
    setGuidesState(next);
  };

  // Die Schalter "Einrasten" und "Raster" gelten je Geraet.
  useEffect(() => saveFlag(SNAP_KEY, snap), [snap]);
  useEffect(() => saveFlag(GRID_KEY, grid), [grid]);
  useEffect(() => saveFlag(STEPS_KEY, showSteps), [showSteps]);

  // Wechselt die Auswahl, gehoert der offene Reihen-Bereich nicht mehr zum markierten Element.
  useEffect(() => {
    setRow((state) => (state.open ? { ...state, open: false, target: null } : state));
  }, [selectionState]);

  // Ohne Auswahl gibt es kein Ausrichten; ein einzelnes Element hat sein Bezugsobjekt erst nach einem Tipp.
  useEffect(() => {
    if (selectionState.length === 0) setAlignOpen(false);
    if (selectionState.length <= 1) setReference(null);
  }, [selectionState]);

  // Das Weiterzeichnen gilt nur, solange der eben gezeichnete Pfeil als einziges markiert ist; ein neuer Pfeil hat noch keinen angefassten Knick.
  useEffect(() => {
    setExtend((current) => (current !== null && selectionState.length === 1 && selectionState[0] === current ? current : null));
    setActiveKnick(null);
  }, [selectionState]);

  // Vollbild: Seite dahinter bleibt stehen; Escape schliesst zuerst das Popover, dann nur das Vollbild (der Dialog darunter bleibt offen, siehe `useEscapeToClose`).
  useScrollLock(expanded);
  useEscapeToClose(expanded, () => {
    if (helpOpen || viewOpen) {
      setHelpOpen(false);
      setViewOpen(false);
    } else {
      setExpanded(false);
    }
  });

  // Der Fokus geht beim Oeffnen aufs Feld (Tastenbefehle) und beim Schliessen zurueck zum Knopf "Gross bearbeiten".
  const wasExpanded = useRef(false);
  useEffect(() => {
    if (expanded) surfaceRef.current?.focus();
    else if (wasExpanded.current) expandRef.current?.focus();
    wasExpanded.current = expanded;
  }, [expanded]);

  // Am Handy zeigt das Blatt bei neuer Auswahl zuerst nur seine Kopfzeile (das Feld bleibt gross), ein Tipp klappt es auf; das Zonenwerkzeug oeffnet es, seine Optionen stehen darin.
  const hasSelection = selectionState.length > 0;
  useEffect(() => setSheetOpen(false), [hasSelection]);
  useEffect(() => {
    if (tool === 'zone') setSheetOpen(true);
  }, [tool]);

  // Ohne Skizze gibt es nichts zurueckzunehmen oder zu markieren.
  useEffect(() => {
    if (value) return;
    setExpanded(false);
    setViewOpen(false);
    history.current = [];
    future.current = [];
    lastKey.current = null;
    lastTap.current = null;
    setUndoDepth(0);
    setRedoDepth(0);
    setInsertOpen(false);
    setTransformOpen(false);
    setBlockDraft(null);
    setSelectionState(NO_SELECTION);
    setMarquee(null);
    guidesRef.current = NO_GUIDES;
    setGuidesState(NO_GUIDES);
    setRow((state) => ({ ...state, open: false, target: null }));
    setPreview(null);
    setDraft(null);
    setAnchor(null);
    setNotice(null);
    setPreviewing(false);
    setAlignOpen(false);
    setReference(null);
    setExtend(null);
    setPile(null);
    stackTap.current = null;
    fitView();
  }, [value, fitView]);

  /* ---------------------------------------------------- Skizze anlegen/entfernen */

  if (!value) {
    return (
      <div data-testid="diagram-empty" className={cn('mx-auto flex max-w-md flex-col items-center gap-3 py-4 text-center', flush && 'max-sm:px-3')}>
        <svg viewBox="0 0 48 32" className="h-12 w-[4.5rem] text-brand-600" aria-hidden="true">
          <rect x="1.5" y="1.5" width="45" height="29" rx="3" fill="rgba(34,197,94,0.15)" stroke="currentColor" strokeWidth="2" />
          <path d="M24 1.5v29" stroke="currentColor" strokeWidth="2" />
          <circle cx="24" cy="16" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-slate-900">Noch keine Skizze</h3>
          <p className="text-sm text-slate-600">Eine Skizze zeigt Aufbau und Laufwege der Übung auf dem Feld. Wähle das Feld – es lässt sich später ändern.</p>
        </div>
        <div className="flex w-full flex-col items-stretch gap-2 text-left sm:flex-row sm:items-end sm:justify-center">
          <div className="sm:w-52">
            <Field label="Feld" hint="Ganzes und halbes Feld mit Linien, die freie Fläche leer (für Spielformen).">
              {(id) => (
                <Select id={id} value={newPitch} disabled={disabled} onChange={(event) => setNewPitch(event.target.value as DiagramPitch)}>
                  {DIAGRAM_PITCH.map((pitch) => (
                    <option key={pitch} value={pitch}>
                      {DIAGRAM_PITCH_LABELS[pitch]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Button type="button" disabled={disabled} onClick={() => onChange({ pitch: newPitch, elements: [] })}>
            Skizze anlegen
          </Button>
        </div>
      </div>
    );
  }

  const pitch = value.pitch;
  const height = PITCH_HEIGHT[pitch];
  const symbolScale = value.symbolScale ?? 1;
  const elements = preview ?? value.elements;
  // Nach aussen veraenderte Skizzen (Elemente fehlen) lassen keine Auswahl ins Leere zeigen.
  const selection = normalizeSelection(selectionState, elements.length);
  const selected = selection.length === 1 ? selection[0]! : null;
  const current = selected !== null ? elements[selected] : undefined;
  // Waehrend des Rahmens zeigt die Markierung schon die Treffer.
  const shown = marquee ? normalizeSelection([...marquee.base, ...elementsInRect(elements, marquee.start, marquee.current)], elements.length) : selection;
  const full = elements.length >= DIAGRAM_MAX_ELEMENTS;
  const toolDef = toolDefOf(tool);
  const steps = diagramSteps({ pitch, elements });
  const hasArrows = steps.length > 0;
  // Bezugsobjekt zum Zentrieren: bei mehreren Elementen das zuletzt angetippte der Auswahl (sonst das letzte Element,
  // das kein Pfeil und keine Masslinie ist - deren "Mitte" ist beliebig und zieht sonst die ganze Gruppe dorthin),
  // bei einem einzelnen das danach angetippte andere Element.
  const referenceIndex =
    selection.length > 1
      ? reference !== null && selection.includes(reference)
        ? reference
        : [...selection].reverse().find((index) => elements[index]?.type !== 'arrow' && elements[index]?.type !== 'measure') ?? selection[selection.length - 1]!
      : reference !== null && reference !== selected && reference < elements.length
        ? reference
        : null;
  /** Das Menue "Ausrichten" ist auf "An Objekt" gestellt: Antippen im Feld waehlt das Bezugsobjekt. */
  const pickingReference = alignOpen && centerMode === 'object' && selection.length > 0;
  // Liegen unter der angetippten Stelle mehrere Elemente, zeigt die Auswahl sie als Liste und das Feld nennt das naechste: nur solange das Element dieser Stelle markiert ist und nichts darunter geaendert wurde.
  const overlap = tool === 'select' && pile && pile.base === value.elements && selected !== null && pile.indices.includes(selected) ? pile : null;
  const overlapNames = overlap ? stackNames(elements, overlap.indices, steps) : [];
  const overlapNext = overlap && selected !== null ? overlapNames[(overlap.indices.indexOf(selected) + 1) % overlap.indices.length] : undefined;

  /* ------------------------------------------------------------ Aenderungen */

  /** Meldet einen neuen Stand der Skizze; Felder ausser Elementen und Symbolgroesse (Feld) bleiben. */
  const emit = (elements: DiagramElement[], symbolScale: DiagramSymbolScale | undefined): void => {
    const latest = valueRef.current;
    if (!latest) return;
    const { symbolScale: _alt, ...rest } = latest;
    onChange(symbolScale === undefined ? { ...rest, elements } : { ...rest, symbolScale, elements });
  };

  const snapshot = (): Snapshot => ({ elements: elementsRef.current, symbolScale: scaleRef.current });

  /** Uebernimmt einen neuen Stand und merkt den alten zum Rueckgaengigmachen. */
  const commit = (next: DiagramElement[], key: string | null = null): void => {
    if (!valueRef.current) return;
    if (key === null || key !== lastKey.current) {
      history.current = [...history.current.slice(-(HISTORY_LIMIT - 1)), snapshot()];
      setUndoDepth(history.current.length);
    }
    // Jede neue Aenderung beendet das Wiederherstellen.
    clearFuture();
    lastKey.current = key;
    elementsRef.current = next;
    emit(next, scaleRef.current);
  };

  const clearFuture = (): void => {
    if (future.current.length === 0) return;
    future.current = [];
    setRedoDepth(0);
  };

  /** Symbolgroesse der ganzen Skizze; "Normal" entfernt das Feld wieder (fehlt = 1). */
  const changeSymbolScale = (scale: DiagramSymbolScale): void => {
    const next = scale === 1 ? undefined : scale;
    if (!valueRef.current || next === scaleRef.current) return;
    history.current = [...history.current.slice(-(HISTORY_LIMIT - 1)), snapshot()];
    setUndoDepth(history.current.length);
    clearFuture();
    lastKey.current = null;
    scaleRef.current = next;
    emit(elementsRef.current, next);
  };

  const undo = (): void => {
    const previous = history.current.pop();
    if (!valueRef.current || !previous) return;
    // Der Stand vor dem Zuruecknehmen kommt auf den Wiederherstellen-Verlauf (gleiche Tiefe wie Rueckgaengig).
    future.current = [...future.current.slice(-(HISTORY_LIMIT - 1)), snapshot()];
    setUndoDepth(history.current.length);
    setRedoDepth(future.current.length);
    lastKey.current = null;
    elementsRef.current = previous.elements;
    scaleRef.current = previous.symbolScale;
    setSelection([]);
    setAnchor(null);
    setNotice(null);
    emit(previous.elements, previous.symbolScale);
  };

  /** Holt den zuletzt zurueckgenommenen Stand zurueck. */
  const redo = (): void => {
    const next = future.current.pop();
    if (!valueRef.current || !next) return;
    history.current = [...history.current.slice(-(HISTORY_LIMIT - 1)), snapshot()];
    setUndoDepth(history.current.length);
    setRedoDepth(future.current.length);
    lastKey.current = null;
    elementsRef.current = next.elements;
    scaleRef.current = next.symbolScale;
    setSelection([]);
    setAnchor(null);
    setNotice(null);
    emit(next.elements, next.symbolScale);
  };

  /** Loescht die ganze Auswahl in einem Schritt. */
  const removeSelected = (): void => {
    if (selection.length === 0) return;
    const gone = new Set(selection);
    commit(elementsRef.current.filter((_, index) => !gone.has(index)));
    setSelection([]);
    setNotice(null);
  };

  /** Markiert alle Elemente; dafuer wechselt der Editor in den Modus "Auswaehlen". */
  const selectAll = (): void => {
    if (tool !== 'select') chooseTool('select');
    setSelection(elementsRef.current.map((_, index) => index));
  };

  const clearAll = (): void => {
    if (elementsRef.current.length === 0) return;
    commit([]);
    setSelection([]);
    setAnchor(null);
    setNotice(null);
  };

  const removeDiagram = (): void => {
    if (window.confirm('Skizze entfernen? Alle Elemente der Skizze gehen verloren.')) onChange(null);
  };

  const changePitch = (next: DiagramPitch): void => {
    const latest = valueRef.current;
    if (!latest) return;
    setAnchor(null);
    onChange({ ...latest, pitch: next, elements: elementsRef.current });
  };

  /** Aendert das markierte Element; `key` fasst Tastenanschlaege zu einem Schritt zusammen. */
  const updateSelected = (change: (element: DiagramElement) => DiagramElement, key: string | null = null): void => {
    if (selected === null) return;
    const base = elementsRef.current;
    const element = base[selected];
    if (!element) return;
    const changed = change(element);
    if (changed === element) return;
    commit(base.map((entry, index) => (index === selected ? changed : entry)), key);
  };

  const chooseTool = (next: ToolId): void => {
    setTool(next);
    setExtend(null);
    setAnchor(null);
    setDraft(null);
    setNotice(null);
    setRow((state) => (state.open ? { ...state, open: false, target: null } : state));
  };

  /** Mehrfachauswahl ein-/ausschalten; eingeschaltet gilt sie im Auswahlmodus, in den der Editor dafuer wechselt. */
  const toggleMulti = (): void => {
    if (!multi && tool !== 'select') chooseTool('select');
    setMulti(!multi);
    setNotice(null);
  };

  /**
   * Mehrere Elemente aendern, ein Schritt: `change` liefert die neue Liste oder dieselbe, wenn nichts zu tun ist.
   * `unchanged` ist der Hinweis fuer den zweiten Fall - ohne ihn sieht ein Knopf, der nichts aendert, nach einem kaputten aus.
   */
  const changeSelection = (change: (base: DiagramElement[], indices: number[]) => DiagramElement[], unchanged?: string): void => {
    if (selection.length === 0) return;
    const base = elementsRef.current;
    const next = change(base, selection);
    if (next === base) {
      if (unchanged) setNotice(unchanged);
      return;
    }
    commit(next);
    setNotice(null);
  };

  /** Setzt die Auswahl nach vorne oder hinten (ein Rueckgaengig-Schritt); die Auswahl folgt den neuen Plaetzen. */
  const orderSelection = (direction: OrderDirection): void => {
    if (selection.length === 0) return;
    const result = reorderSelection(elementsRef.current, selection, direction);
    if (!result.ok) {
      setNotice(result.problem);
      return;
    }
    commit(result.elements);
    bodyTap.current = null;
    setSelection(result.selection);
    if (reference !== null) setReference(result.moved(reference));
    setNotice(null);
  };

  /** Ein Klick in der Liste "Hier liegen" markiert genau dieses Element. */
  const pickFromPile = (index: number): void => {
    bodyTap.current = null;
    setSelection([index]);
    setNotice(null);
  };

  const duplicateSelection = (): void => {
    if (selection.length === 0) return;
    const base = elementsRef.current;
    if (base.length + selection.length > DIAGRAM_MAX_ELEMENTS) {
      setNotice(`Zum Duplizieren fehlt der Platz: ${selection.length} Kopien zu ${base.length} Elementen wären mehr als ${DIAGRAM_MAX_ELEMENTS}. Lösche ein paar Elemente.`);
      return;
    }
    const copies = copyElements(base, selection, duplicateOffset(base, selection, pitch));
    commit([...base, ...copies]);
    setSelection(copies.map((_, offset) => base.length + offset));
    setNotice(null);
  };

  const toggleSmallSelection = (): void =>
    changeSelection((base, indices) => {
      const targets = indices.filter((index) => canBeSmall(base[index]));
      if (targets.length === 0) return base;
      const makeSmall = !targets.every((index) => (base[index] as { size?: 'S' }).size === 'S');
      return base.map((element, index) => {
        if (!targets.includes(index) || element.type === 'goal' || element.type === 'zone' || element.type === 'arrow' || element.type === 'measure') return element;
        const { size: _alt, ...rest } = element;
        return makeSmall ? { ...rest, size: 'S' } : rest;
      });
    });

  /**
   * Zentriert die Auswahl waagrecht (`x`) oder senkrecht (`y`): am Feld - als Gruppe (Form bleibt) oder jedes Element
   * einzeln auf die Mittellinie - oder am Bezugsobjekt.
   */
  const centerSelection = (axis: 'x' | 'y', scope: CenterScope): void => {
    if (selection.length === 0) return;
    if (centerMode === 'field') {
      if (scope === 'group') changeSelection((base, indices) => centerGroupOnField(base, indices, axis), NOTHING_TO_CENTER_GROUP);
      else changeSelection((base, indices) => centerOnField(base, indices, axis), NOTHING_TO_CENTER);
      return;
    }
    if (referenceIndex === null) {
      setNotice('Tippe zuerst das Element an, an dessen Mitte ausgerichtet wird.');
      return;
    }
    changeSelection((base, indices) => centerOnObject(base, indices, referenceIndex, axis), NOTHING_TO_CENTER);
  };

  /** Toleranz des Einrastens in Feldkoordinaten; `null`, wenn Einrasten aus ist. */
  const snapToleranceNow = (): { x: number; y: number } | null => (snap ? snapTolerance(pixelsPerUnit(viewport.rect(), pitch, viewport.current()), pitch) : null);

  /** Weite des Rasters in Feldprozent (1 Einheit, gezoomt ab 3x eine halbe); `null`, wenn das Raster aus ist. */
  const gridStepNow = (): number | null => (grid ? gridStepFor(viewport.current().zoom) : null);

  /** Endpunkt einer Masslinie, eingerastet an Elementen oder fluchtend zum anderen Ende. */
  const snapMeasure = (point: FieldPoint, other: FieldPoint | null, skip: number[] = []): FieldPoint =>
    snapMeasurePoint(point, other, elementsRef.current, skip, snapToleranceNow(), pitch);

  const alignSelection = (mode: AlignMode): void =>
    changeSelection((base, indices) => alignElements(base, indices, mode), 'Die markierten Elemente liegen hier schon bündig – es ändert sich nichts.');
  const distributeSelection = (axis: 'x' | 'y'): void =>
    changeSelection((base, indices) => distributeElements(base, indices, axis), 'Die markierten Elemente haben hier schon gleiche Abstände – es ändert sich nichts.');
  const changeSelectionTeam = (team: DiagramTeam): void => changeSelection((base, indices) => changeTeam(base, indices, team));
  const changeSelectionFacing = (facing: number | null): void => changeSelection((base, indices) => setFacing(base, indices, facing));

  /* ------------------------------------------------------------------ Reihe */

  // Der Reihen-Bereich gilt nur fuer genau ein markiertes Element.
  const rowActive = row.open && selected !== null && isRowSource(current);
  const rowPlan = rowActive && selected !== null ? planRow(value.elements, selected, row, pitch) : null;

  const toggleRow = (): void => setRow((state) => ({ ...state, open: !state.open, target: null }));

  const applyRow = (): void => {
    if (!rowPlan || rowPlan.problem !== null || selected === null) return;
    const base = elementsRef.current;
    commit([...base, ...rowPlan.copies]);
    // Das ganze Ergebnis ist markiert: so laesst es sich gleich ausrichten oder verschieben.
    setSelection([selected, ...rowPlan.copies.map((_, offset) => base.length + offset)]);
    setRow((state) => ({ ...state, open: false, target: null }));
    setNotice(null);
  };

  /** Haengt ein Element an; ist die Skizze voll, gibt es einen Hinweis statt eines Fehlers. */
  const append = (element: DiagramElement, key: string | null = null): number | null => {
    const base = elementsRef.current;
    if (base.length >= DIAGRAM_MAX_ELEMENTS) {
      setNotice(LIMIT_NOTICE);
      return null;
    }
    commit([...base, element], key);
    setSelection([base.length]);
    setNotice(null);
    return base.length;
  };

  /**
   * Haengt eine Gruppe von Elementen an (ein Rueckgaengig-Schritt) und markiert sie, damit sie sich gleich verschieben laesst;
   * passt sie nicht mehr hinein, gibt es den Hinweis `tooMany` statt einer Aenderung. Gemeinsam fuer Formationen und Bausteine.
   */
  const appendGroup = (added: DiagramElement[], tooMany: (count: number, used: number) => string): void => {
    const base = elementsRef.current;
    if (base.length + added.length > DIAGRAM_MAX_ELEMENTS) {
      setNotice(tooMany(added.length, base.length));
      return;
    }
    commit([...base, ...added]);
    setSelection(added.map((_, offset) => base.length + offset));
    setNotice(null);
  };

  /** Haengt die Spieler einer Formation an; passt sie nicht mehr hinein, gibt es einen Hinweis. */
  const insertFormation = (added: DiagramElement[]): void => {
    setFormationOpen(false);
    appendGroup(added, (count, used) => `Die Formation hat ${count} Spieler – bei ${used} Elementen wären das mehr als ${DIAGRAM_MAX_ELEMENTS}. Lösche ein paar Elemente.`);
  };

  /** Haengt einen Aufbau oder Baustein an; die Stelle ist danach verbraucht (der naechste kommt wieder in die Mitte). */
  const insertBlock = (added: DiagramElement[]): void => {
    setInsertOpen(false);
    lastTap.current = null;
    if (tool !== 'select') chooseTool('select');
    appendGroup(added, (count, used) => `Der Baustein hat ${count} Elemente – bei ${used} Elementen wären das mehr als ${DIAGRAM_MAX_ELEMENTS}. Lösche ein paar Elemente.`);
  };

  /** Spiegelt oder dreht die Auswahl, ohne Auswahl die ganze Skizze; ein Rueckgaengig-Schritt je Aktion. */
  const applyTransform = (op: TransformOp): void => {
    const base = elementsRef.current;
    const result = transformElements(base, selection.length > 0 ? selection : null, op, pitch);
    if (!result.ok) {
      setNotice(result.problem);
      return;
    }
    if (result.unchanged) {
      setNotice('Das ändert hier nichts – die Elemente sehen danach genauso aus.');
      return;
    }
    commit(result.elements);
    setNotice(result.shifted ? 'Gedreht und ins Feld geschoben, damit alles hineinpasst.' : null);
  };

  /** Oeffnet "Als Baustein speichern" mit den markierten Elementen. */
  const openSaveBlock = (): void => {
    if (selection.length === 0) return;
    setBlockDraft(blockFromSelection(elementsRef.current, selection, pitch));
  };

  /* ------------------------------------------------------- Zeigerbedienung */

  /** Feldpunkt unter dem Zeiger: mit eingeschaltetem Raster aufs Raster gerastet, sonst (und mit `raw`) auf halbe Einheiten gerundet. */
  const fieldPoint = (event: ReactPointerEvent<SVGSVGElement>, raw = false): FieldPoint =>
    clientToField(event.clientX, event.clientY, viewport.rect(), pitch, viewport.current(), raw ? null : gridStepNow());

  /**
   * Bricht die laufende Ein-Finger-Aktion ab, ohne Spuren: ein gerade gesetztes Element
   * verschwindet samt Rueckgaengig-Schritt wieder, Ziehen und Zeichnen verwerfen ihre Vorschau.
   */
  cancelRef.current = (): void => {
    const drag = dragRef.current;
    dragRef.current = null;
    setPreview(null);
    setDraft(null);
    setMarquee(null);
    setGuides(NO_GUIDES);
    if (drag?.mode !== 'move' || !drag.placement) return;
    const { before, selectedBefore, historyBefore, futureBefore } = drag.placement;
    history.current = history.current.slice(0, historyBefore);
    future.current = futureBefore;
    setUndoDepth(history.current.length);
    setRedoDepth(futureBefore.length);
    lastKey.current = null;
    elementsRef.current = before;
    setSelection(selectedBefore);
    setNotice(null);
    emit(before, scaleRef.current);
  };

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>): void => {
    // Ein zweiter Finger macht aus der Bedienung eine Zoom-Geste (und bricht die Aktion des ersten ab).
    if (viewport.gestureDown(event)) return;
    if (dragRef.current) {
      // Ein neuer erster Zeiger, obwohl noch eine Aktion laeuft: Deren "Hoch" ist nie angekommen
      // (z. B. Fenster verlassen). Sie wird verworfen, damit nichts haengen bleibt.
      if (event.isPrimary !== true) return;
      dragRef.current = null;
      setPreview(null);
      setDraft(null);
      setMarquee(null);
      setGuides(NO_GUIDES);
    }
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const point = fieldPoint(event);
    /** Ohne Raster gerundeter Punkt: Verschieben rechnet damit, das Raster rastet erst das Ergebnis ein. */
    const rawPoint = fieldPoint(event, true);
    const target = event.target as Element;
    const capture = (): void => {
      try {
        svgRef.current?.setPointerCapture(event.pointerId);
      } catch {
        // Ohne Zeigererfassung laufen die Ereignisse weiter, solange der Zeiger ueber dem Feld bleibt.
      }
    };

    /** Ziehen auf leerer Flaeche schwenkt, solange gezoomt ist. */
    const startPan = (): boolean => {
      if (!isZoomed(viewport.current())) return false;
      const grab = viewport.panStart(event.clientX, event.clientY);
      if (!grab) return false;
      capture();
      dragRef.current = { mode: 'pan', pointerId: event.pointerId, grab, startX: event.clientX, startY: event.clientY, moved: false };
      return true;
    };

    // Ohne Bearbeitungsrecht bleibt nur das Schwenken.
    if (disabled) {
      startPan();
      return;
    }
    surfaceRef.current?.focus({ preventScroll: true });
    lastTap.current = rawPoint;
    const base = elementsRef.current;

    if (tool === 'select') {
      // Reihe "bis zu einem Punkt": Antippen setzt den Zielpunkt, Ziehen rueckt ihn zurecht.
      if (rowActive && row.direction === 'target') {
        capture();
        dragRef.current = { mode: 'target', pointerId: event.pointerId };
        setRow((state) => ({ ...state, target: point }));
        return;
      }
      // Mehrfachauswahl: Umschalter "Mehrfach", am Desktop Umschalt-/Strg-/Cmd-Klick.
      const additive = multi || event.shiftKey || event.ctrlKey || event.metaKey;
      // Alles, was unter dem Tipp liegt (Spieler, Pfeile, Zonen ...): der erste Tipp waehlt das erste, weitere Tipps nahe derselben Stelle der Reihe nach die anderen.
      const hitAttribute = target.closest('[data-index]')?.getAttribute('data-index');
      const hitIndex = hitAttribute === null || hitAttribute === undefined || Number(hitAttribute) >= base.length ? null : Number(hitAttribute);
      // Das Bild entscheidet, ob ueberhaupt etwas (oder ein Griff) getroffen wurde; die Rechnung liefert dann, was alles darunter liegt.
      const onHandle = target.closest('[data-handle]') !== null;
      const stack = hitIndex === null && !onHandle ? [] : stackAt(base, rawPoint, { pitch, zoom: viewport.zoom, scale: symbolScale }, hitIndex);
      const lastSpot = stackTap.current;
      const sameSpot = lastSpot !== null && fieldDistance(lastSpot, rawPoint, pitch) <= DOUBLE_TAP_DISTANCE;
      stackTap.current = stack.length === 0 ? null : sameSpot ? lastSpot : rawPoint;
      setPile(stack.length > 1 && !additive ? { indices: stack, base } : null);
      /** Der Tipp wiederholt den vorigen an derselben Stelle: das markierte Element bleibt unter dem Finger (Ziehen verschiebt es), ein Tipp ohne Ziehen geht weiter. */
      const repeat = !additive && sameSpot && selected !== null && selection.length === 1 && stack.includes(selected);
      const nextInStack = repeat ? stack[(stack.indexOf(selected) + 1) % stack.length]! : null;
      const handle = target.closest('[data-handle]')?.getAttribute('data-handle');
      const marked = selected !== null ? base[selected] : undefined;
      if (handle && selected !== null && marked && (marked.type === 'arrow' || marked.type === 'measure' || marked.type === 'player' || marked.type === 'zone')) {
        capture();
        if (handle.startsWith('knick-') && marked.type === 'arrow') {
          // Doppeltipp auf einen Knickpunkt entfernt ihn; ein einfacher Tipp macht ihn zum angefassten Knick.
          const knick = Number(handle.slice(6));
          const last = knickTap.current;
          const now = Date.now();
          setActiveKnick(knick);
          if (last && last.index === selected && last.knick === knick && now - last.time <= DOUBLE_TAP_MS) {
            knickTap.current = null;
            setActiveKnick(null);
            commit(base.map((entry, index) => (index === selected ? removeKnick(marked, knick) : entry)));
            return;
          }
          knickTap.current = { index: selected, knick, time: now };
        }
        if (handle === 'via' && marked.type === 'arrow') {
          // Doppeltipp auf den Biegegriff: gerade machen.
          const last = viaTap.current;
          const now = Date.now();
          if (marked.via && last && last.index === selected && now - last.time <= DOUBLE_TAP_MS) {
            viaTap.current = null;
            commit(base.map((entry, index) => (index === selected ? straightenArrow(marked) : entry)));
            return;
          }
          viaTap.current = { index: selected, time: now };
        }
        // Ein Tipp (ohne Ziehen) auf Anfang oder Ende wiederholt den Tipp an der Stelle: weiter im Stapel (Biegegriff und Knicke haben ihren Doppeltipp).
        const cycle = nextInStack !== null && nextInStack !== selected && handle !== 'via' && !handle.startsWith('knick-') ? nextInStack : undefined;
        dragRef.current = { mode: 'handle', pointerId: event.pointerId, index: selected, end: handle, base, ...(cycle !== undefined ? { cycle } : {}) };
        return;
      }
      if (stack.length === 0) {
        // Leere Flaeche: gezoomt schwenkt ein Zeiger (ein Tipp hebt die Markierung auf), ausser der Rahmen ist
        // ausdruecklich gewollt (Umschalter oder Umschalttaste). Ungezoomt zieht Ziehen den Rahmen auf.
        if (isZoomed(viewport.current()) && !(multi || event.shiftKey)) {
          if (!startPan()) setSelection([]);
          return;
        }
        const keep = additive ? selection : [];
        if (!additive) setSelection([]);
        capture();
        dragRef.current = { mode: 'marquee', pointerId: event.pointerId, start: rawPoint, base: keep };
        setMarquee({ start: rawPoint, current: rawPoint, base: keep });
        return;
      }
      // Der Tipp gilt dem ersten des Stapels - bei einer Gruppe einem ihrer Mitglieder, damit Ziehen die ganze Gruppe verschiebt.
      const index = (!additive && selection.length > 1 ? stack.find((entry) => selection.includes(entry)) : undefined) ?? stack[0]!;
      // Zentrieren "An Objekt" bei einem einzelnen markierten Element: ein Tipp auf ein anderes Element waehlt es als Bezug.
      if (pickingReference && selection.length === 1 && !additive) {
        const other = stack.find((entry) => entry !== selected);
        if (other !== undefined) {
          setReference(other);
          return;
        }
      }
      capture();
      const inSelection = selection.includes(index);
      let indices: number[];
      let tap: { kind: 'remove' | 'narrow' | 'reference' | 'cycle'; index: number } | null = null;
      if (repeat && selected !== null) {
        // Das markierte Element bleibt; ein Tipp ohne Ziehen wechselt zum naechsten im Stapel - ausser es ist ein schneller Doppeltipp auf Text
        // (bearbeitet den Text) oder Linie (setzt einen Knick), den der Tipp-Abschluss in `bodyTapped` erkennt.
        const last = bodyTap.current;
        const editing =
          (marked?.type === 'text' || marked?.type === 'arrow') &&
          last !== null &&
          last.index === selected &&
          Date.now() - last.time <= DOUBLE_TAP_MS &&
          fieldDistance(last.point, point, pitch) <= DOUBLE_TAP_DISTANCE;
        indices = [selected];
        if (nextInStack !== null && nextInStack !== selected && !editing) tap = { kind: 'cycle', index: nextInStack };
      } else if (additive) {
        // Antippen nimmt ein markiertes Element heraus (erst beim Loslassen: wer zieht, verschiebt die Gruppe).
        if (inSelection) {
          indices = selection;
          tap = { kind: 'remove', index };
        } else {
          indices = normalizeSelection([...selection, index], base.length);
          setSelection(indices);
          // Das zuletzt hinzugefuegte Element ist das Bezugsobjekt.
          setReference(index);
        }
      } else if (inSelection && selection.length > 1) {
        // Ziehen an einem markierten Element bewegt die ganze Gruppe; ein Tipp verengt auf dieses Element
        // - beim Zentrieren "An Objekt" macht er es stattdessen zum Bezugsobjekt.
        indices = selection;
        tap = { kind: pickingReference ? 'reference' : 'narrow', index };
      } else {
        indices = [index];
        setSelection(indices);
      }
      dragRef.current = {
        mode: 'move',
        pointerId: event.pointerId,
        indices,
        start: rawPoint,
        screen: { x: event.clientX, y: event.clientY },
        moved: false,
        base,
        key: null,
        placement: null,
        tap,
      };
      return;
    }

    // Werkzeug "Text" auf einem vorhandenen Text: markieren, mit Doppeltipp bearbeiten - statt einen zweiten daruebergelegten zu setzen.
    if (tool === 'text') {
      const hit = target.closest('[data-index]');
      const hitIndex = hit?.getAttribute('data-type') === 'text' ? Number(hit.getAttribute('data-index')) : null;
      if (hitIndex !== null) {
        setSelection([hitIndex]);
        const last = bodyTap.current;
        const now = Date.now();
        if (last && last.index === hitIndex && now - last.time <= DOUBLE_TAP_MS && fieldDistance(last.point, point, pitch) <= DOUBLE_TAP_DISTANCE) {
          bodyTap.current = null;
          setFocusText((count) => count + 1);
        } else {
          bodyTap.current = { index: hitIndex, time: now, point };
        }
        return;
      }
    }

    // Ein Pfeil, der mit zwei Tipps gezeichnet wurde, waechst mit jedem weiteren Tipp um einen Knickpunkt.
    if (extend !== null && arrowKindOf(tool) && base[extend]?.type === 'arrow') {
      capture();
      dragRef.current = { mode: 'extend', pointerId: event.pointerId, index: extend };
      const end = (base[extend] as Extract<DiagramElement, { type: 'arrow' }>).to;
      setDraft({ start: { x: end[0], y: end[1] }, current: point });
      return;
    }

    capture();
    if (tool === 'zone' || tool === 'measure' || arrowKindOf(tool)) {
      const start = tool === 'measure' ? snapMeasure(point, anchor) : point;
      dragRef.current = { mode: 'draw', pointerId: event.pointerId, start, tool, anchor, path: [start] };
      setDraft({ start, current: start });
      return;
    }

    // Einzelelemente: sofort setzen; wer weiter zieht, rueckt es noch zurecht (ein Schritt).
    let element: DiagramElement;
    if (tool.startsWith('player')) {
      const team = tool.slice(6) as DiagramTeam;
      element = { type: 'player', team, x: point.x, y: point.y, label: nextPlayerLabel(base, team) };
    } else if (tool === 'goal') {
      element = { type: 'goal', x: point.x, y: point.y, size: defaultGoalSize(pitch), rotation: goalRotationAt(point, pitch) };
    } else if (tool === 'hurdle') {
      element = { type: 'hurdle', x: point.x, y: point.y, rotation: 0 };
    } else if (tool === 'text') {
      element = { type: 'text', x: point.x, y: point.y, text: 'Text' };
    } else {
      element = { type: tool === 'cone' ? 'cone' : tool === 'pole' ? 'pole' : tool === 'ring' ? 'ring' : 'ball', x: point.x, y: point.y };
    }
    const index = base.length;
    const key = `place-${index}`;
    const placement: Placement = { before: base, selectedBefore: selection, historyBefore: history.current.length, futureBefore: future.current };
    if (append(element, key) === null) return;
    dragRef.current = {
      mode: 'move',
      pointerId: event.pointerId,
      indices: [index],
      start: rawPoint,
      screen: { x: event.clientX, y: event.clientY },
      moved: false,
      base: [...base, element],
      key,
      placement,
      tap: null,
    };
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>): void => {
    if (viewport.gestureMove(event)) return;
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.mode === 'pan') {
      if (!drag.moved && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < PAN_THRESHOLD) return;
      drag.moved = true;
      viewport.panMove(drag.grab, event.clientX, event.clientY);
      return;
    }
    const point = fieldPoint(event);
    if (drag.mode === 'draw') {
      const end = drawEnd(drag, point);
      // Den Weg merken (Freihand): nur Punkte, die sich merklich vom letzten unterscheiden.
      const last = drag.path[drag.path.length - 1]!;
      if (arrowKindOf(drag.tool) && fieldDistance(last, end, pitch) >= 1) drag.path.push(end);
      setDraft({ start: drag.start, current: end, ...(drag.path.length > 2 ? { path: drag.path } : {}) });
      return;
    }
    if (drag.mode === 'marquee') {
      setMarquee({ start: drag.start, current: fieldPoint(event, true), base: drag.base });
      return;
    }
    if (drag.mode === 'target') {
      setRow((state) => ({ ...state, target: point }));
      return;
    }
    if (drag.mode === 'extend') {
      const arrow = elementsRef.current[drag.index];
      if (arrow?.type === 'arrow') setDraft({ start: { x: arrow.to[0], y: arrow.to[1] }, current: point });
      return;
    }
    if (drag.mode === 'handle') {
      const element = drag.base[drag.index];
      const showHandleResult = (moved: DiagramElement): void =>
        setPreview(JSON.stringify(moved) === JSON.stringify(element) ? null : drag.base.map((entry, index) => (index === drag.index ? moved : entry)));
      if (element?.type === 'measure') {
        if (drag.end !== 'from' && drag.end !== 'to') return;
        const other = drag.end === 'from' ? element.to : element.from;
        showHandleResult(moveArrowEnd(element, drag.end, snapMeasure(point, { x: other[0], y: other[1] }, [drag.index])));
        return;
      }
      // Drehgriff der Blickrichtung: rastet (mit "Einrasten") auf 45 Grad; liegt der Zeiger auf dem Spieler, bleibt es beim alten Wert.
      if (element?.type === 'player' && drag.end === 'facing') {
        const facing = facingFromPoint({ x: element.x, y: element.y }, point, pitch, snap ? FACING_SNAP_DEGREES : null);
        showHandleResult(facing === null ? element : withFacing(element, facing));
        return;
      }
      if (element?.type === 'zone' && drag.end.startsWith('corner-')) {
        showHandleResult(resizeZone(element, drag.end.slice(7) as ZoneCorner, point));
        return;
      }
      if (!element || element.type !== 'arrow') return;
      if (drag.end.startsWith('knick-')) {
        showHandleResult(moveKnick(element, Number(drag.end.slice(6)), point));
        return;
      }
      if (drag.end !== 'from' && drag.end !== 'to' && drag.end !== 'via') return;
      const moved = drag.end === 'via' ? bendArrow(element, point, pitch) : moveArrowEnd(element, drag.end, point);
      // Zurueck an der Ausgangsstelle (oder Pfeil ohne Laenge): nichts zu uebernehmen.
      setPreview(JSON.stringify(moved) === JSON.stringify(element) ? null : drag.base.map((entry, index) => (index === drag.index ? moved : entry)));
      return;
    }
    // Verschieben, einzeln oder als Gruppe: erst ab einer kleinen Strecke, damit ein zitternder Finger beim Tippen nichts verrueckt.
    if (!drag.moved) {
      if (Math.hypot(event.clientX - drag.screen.x, event.clientY - drag.screen.y) < MOVE_THRESHOLD) return;
      drag.moved = true;
    }
    // Rohe Punkte: der Abstand zum Druckpunkt zaehlt, das Ergebnis rastet am Objekt (Vorrang) oder am Raster ein.
    const raw = fieldPoint(event, true);
    const tolerance = snapToleranceNow();
    const result = moveGroup(drag.base, drag.indices, raw.x - drag.start.x, raw.y - drag.start.y, { tolerance: tolerance ?? undefined, grid: gridStepNow() });
    setGuides(result.guides);
    setPreview(result.elements === drag.base ? null : result.elements);
  };

  /** Ende beim Zeichnen: Masslinien rasten ein (zum Anfang hin, oder zum ersten getippten Punkt), alles andere liegt, wo der Zeiger ist. */
  const drawEnd = (drag: Extract<Drag, { mode: 'draw' }>, point: FieldPoint): FieldPoint => {
    if (drag.tool !== 'measure') return point;
    const fromAnchor = drag.anchor !== null && fieldDistance(drag.start, point, pitch) < DRAG_THRESHOLD;
    return snapMeasure(point, fromAnchor ? drag.anchor : drag.start);
  };

  const finishDraw = (drag: Extract<Drag, { mode: 'draw' }>, end: FieldPoint): void => {
    const kind = arrowKindOf(drag.tool);
    /** `tapped`: Anfang und Ende wurden getippt (nicht gezogen) - dann waechst der Pfeil mit weiteren Tipps. */
    const place = (a: FieldPoint, b: FieldPoint, tapped: boolean): void => {
      if (drag.tool === 'measure') {
        append({ type: 'measure', from: [a.x, a.y], to: [b.x, b.y] });
        return;
      }
      if (kind) {
        // Ein Schritt zum Rueckgaengigmachen fuer den ganzen Pfeil samt Knickpunkten (gleicher Schluessel).
        const index = append({ type: 'arrow', kind, from: [a.x, a.y], to: [b.x, b.y] }, `draw-${elementsRef.current.length}`);
        // Pass, Schuss und hoher Ball bleiben gerade: kein "Fertig"-Modus, der naechste Tipp beginnt einen neuen Pfeil.
        if (index !== null && tapped && !DRAWN_STRAIGHT.includes(kind)) setExtend(index);
        return;
      }
      const zone = zoneFromPoints(a, b);
      if (zone.w < ZONE_MIN_SIZE || zone.h < ZONE_MIN_SIZE) {
        setNotice('Die Zone ist zu schmal – wähle zwei Ecken, die auf beiden Achsen auseinanderliegen.');
        return;
      }
      append(applyZoneLook(zone, zoneLook));
    };

    if (fieldDistance(drag.start, end, pitch) >= DRAG_THRESHOLD) {
      // Gezogen: Anfang ist der Druckpunkt, ein vorher getippter Anfang verfaellt.
      setAnchor(null);
      // Eine deutliche Kurve wird zum weichen Freihand-Pfeil, ein fast gerades Ziehen bleibt ein gerader Pfeil.
      const freehand = kind ? freehandArrow(kind, pathTo(drag.path, end), pitch) : null;
      if (freehand) {
        append(freehand, `draw-${elementsRef.current.length}`);
        return;
      }
      place(drag.start, end, false);
    } else if (drag.anchor && fieldDistance(drag.anchor, end, pitch) >= DRAG_THRESHOLD) {
      setAnchor(null);
      place(drag.anchor, end, true);
    } else if (!drag.anchor) {
      setAnchor(end);
      setNotice(null);
    }
  };

  /** Ein weiterer Tipp beim Zeichnen: neuer Knickpunkt am Ende des Pfeils; ein Tipp auf den letzten Punkt beendet ihn. */
  const extendByTap = (index: number, end: FieldPoint): void => {
    const arrow = elementsRef.current[index];
    if (arrow?.type !== 'arrow') {
      setExtend(null);
      return;
    }
    if (fieldDistance({ x: arrow.to[0], y: arrow.to[1] }, end, pitch) < DRAG_THRESHOLD) {
      setExtend(null);
      setNotice(null);
      return;
    }
    const next = extendArrow(arrow, end);
    if (!next) {
      setNotice(`Mehr als ${DIAGRAM_ARROW_MAX_POINTS} Knickpunkte hat ein Pfeil nicht – er ist fertig.`);
      setExtend(null);
      return;
    }
    setNotice(null);
    commit(elementsRef.current.map((entry, at) => (at === index ? next : entry)), `draw-${index}`);
  };

  /**
   * Tipp auf Linie oder Text des einzigen markierten Elements (ohne zu ziehen): Ein zweiter Tipp kurz danach setzt
   * auf einer Pfeillinie einen Knickpunkt und bearbeitet bei einem Text den Wortlaut.
   */
  const bodyTapped = (index: number, point: FieldPoint): void => {
    const element = elementsRef.current[index];
    const last = bodyTap.current;
    const now = Date.now();
    if (!element || !last || last.index !== index || now - last.time > DOUBLE_TAP_MS || fieldDistance(last.point, point, pitch) > DOUBLE_TAP_DISTANCE) {
      bodyTap.current = element ? { index, time: now, point } : null;
      return;
    }
    bodyTap.current = null;
    if (element.type === 'text') {
      setFocusText((count) => count + 1);
      return;
    }
    if (element.type !== 'arrow') return;
    const next = insertKnick(element, point, pitch);
    if (!next) {
      setNotice(element.kind === 'LOFT' ? 'Ein hoher Ball hat keine Knickpunkte.' : canAddKnick(element) ? 'An dieser Stelle sitzt schon ein Punkt.' : `Mehr als ${DIAGRAM_ARROW_MAX_POINTS} Knickpunkte hat ein Pfeil nicht.`);
      return;
    }
    setNotice(null);
    commit(elementsRef.current.map((entry, at) => (at === index ? next : entry)));
  };

  const onPointerUp = (event: ReactPointerEvent<SVGSVGElement>): void => {
    if (viewport.gestureUp(event)) return;
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      svgRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      // Die Erfassung war schon beendet.
    }
    if (drag.mode === 'pan') {
      // Ein Tipp auf leere Flaeche (ohne zu schwenken) hebt die Markierung auf.
      if (!drag.moved) setSelection([]);
      return;
    }
    if (drag.mode === 'draw') {
      setDraft(null);
      finishDraw(drag, drawEnd(drag, fieldPoint(event)));
      return;
    }
    if (drag.mode === 'extend') {
      setDraft(null);
      extendByTap(drag.index, fieldPoint(event));
      return;
    }
    if (drag.mode === 'marquee') {
      const count = elementsRef.current.length;
      setMarquee(null);
      setSelection(normalizeSelection([...drag.base, ...elementsInRect(elementsRef.current, drag.start, fieldPoint(event, true))], count));
      return;
    }
    if (drag.mode === 'target') return;
    const result = previewRef.current;
    setPreview(null);
    setGuides(NO_GUIDES);
    // Wer den Griff gezogen hat, hat nicht doppelt getippt.
    if (result && drag.mode === 'handle' && drag.end === 'via') viaTap.current = null;
    // Ein eben gesetzter Text bekommt sofort den Fokus im Eingabefeld.
    const placedText = drag.mode === 'move' && drag.placement !== null && drag.base[drag.indices[0]!]?.type === 'text';
    if (result) {
      commit(result, drag.mode === 'move' ? drag.key : null);
      // Verschoben oder verformt: die Stelle des Stapels stimmt nicht mehr.
      stackTap.current = null;
      if (placedText) setFocusText((count) => count + 1);
      return;
    }
    if (placedText) setFocusText((count) => count + 1);
    if (drag.mode === 'handle' && drag.cycle !== undefined) {
      // Tipp auf den Griff, ohne zu ziehen: das naechste Element des Stapels.
      bodyTap.current = null;
      setSelection([drag.cycle]);
    } else if (drag.mode === 'move' && drag.tap && !drag.moved) {
      const { kind, index } = drag.tap;
      if (kind === 'cycle') bodyTap.current = null;
      if (kind === 'reference') setReference(index);
      else setSelection(kind === 'remove' ? selection.filter((entry) => entry !== index) : [index]);
    } else if (drag.mode === 'move' && !drag.moved && drag.placement === null && drag.indices.length === 1) {
      bodyTapped(drag.indices[0]!, fieldPoint(event));
    }
  };

  const onPointerCancel = (event: ReactPointerEvent<SVGSVGElement>): void => {
    if (viewport.gestureUp(event)) return;
    dragRef.current = null;
    setPreview(null);
    setDraft(null);
    setMarquee(null);
    setGuides(NO_GUIDES);
  };

  /* -------------------------------------------------------------- Tastatur */

  // Nur auf der Zeichenflaeche: Tastenanschlaege in den Feldern des Formulars
  // erreichen diese Funktion nicht.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabled) return;
    const command = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();
    if (command && !event.shiftKey && key === 'z') {
      event.preventDefault();
      undo();
    } else if (command && ((key === 'y' && !event.shiftKey) || (key === 'z' && event.shiftKey))) {
      // Wiederherstellen: Strg+Y und Strg+Umschalt+Z.
      event.preventDefault();
      redo();
    } else if (command && !event.shiftKey && event.key.toLowerCase() === 'a') {
      // Alles markieren - nur hier, nicht im ganzen Formular.
      event.preventDefault();
      selectAll();
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      if (selection.length === 0) return;
      event.preventDefault();
      removeSelected();
    } else if (event.key.startsWith('Arrow') && selection.length > 0) {
      event.preventDefault();
      const step = event.shiftKey ? 5 : 1;
      const dx = event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0;
      const dy = event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0;
      const base = elementsRef.current;
      const moved = translateGroup(base, selection, dx, dy);
      if (moved !== base) commit(moved, `nudge-${selection.join(',')}`);
    }
  };

  /* ---------------------------------------------------------------- Ansicht */

  // Vorschau des Ablaufs: derselbe Player wie in der Anzeige, die Bearbeitung ruht.
  if (previewing && hasArrows) {
    const previewView = (
      <div className="space-y-3" data-testid="diagram-preview">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">Vorschau des Ablaufs – so wird die Skizze abgespielt.</p>
          <Button type="button" variant="secondary" size="sm" onClick={() => setPreviewing(false)}>
            Zurück zum Bearbeiten
          </Button>
        </div>
        <DiagramPlayer diagram={value} className={cn('mx-auto', expanded ? 'max-h-[75dvh] max-w-3xl' : 'max-h-[60vh] max-w-lg')} />
      </div>
    );
    // Im Vollbild bleibt auch die Vorschau auf der grossen Ebene (die Seite dahinter ist gesperrt, Escape schliesst sie).
    return expanded
      ? createPortal(
          <div role="dialog" aria-modal="true" aria-label="Vorschau des Ablaufs" data-testid="diagram-expanded" className="fixed inset-0 z-50 overflow-y-auto bg-slate-50 p-3 print:hidden">
            {previewView}
          </div>,
          document.body,
        )
      : previewView;
  }

  const ordered = elements
    .map((element, index) => ({ element, index }))
    .sort((a, b) => LAYER[a.element.type] - LAYER[b.element.type]);

  const draftKind = arrowKindOf(tool);
  let ghost: DiagramElement | null = null;
  if (draft) {
    // Ein Pfeil, der gerade als Freihand-Kurve gezogen wird, zeigt schon die weiche Kurve, die daraus wird.
    const freehand = draftKind && draft.path ? freehandArrow(draftKind, pathTo(draft.path, draft.current), pitch) : null;
    ghost = freehand
      ? freehand
      : draftKind
      ? { type: 'arrow', kind: draftKind, from: [draft.start.x, draft.start.y], to: [draft.current.x, draft.current.y] }
      : tool === 'measure'
        ? { type: 'measure', from: [draft.start.x, draft.start.y], to: [draft.current.x, draft.current.y] }
        : applyZoneLook(zoneFromPoints(draft.start, draft.current), zoneLook);
  }

  /** Was ein Tipp oder Ziehen im Modus "Auswaehlen" gerade bewirkt - je nach Markierung. */
  const selectHint = (): string => {
    if (selection.length > 1) return 'Ziehen verschiebt die ganze Auswahl, die Pfeiltasten schieben sie, Entf löscht sie. Die Befehle stehen unter dem Feld in der Auswahl.';
    switch (current?.type) {
      case undefined:
        return viewport.zoomed
          ? 'Element antippen zum Markieren, ziehen zum Verschieben. Auf leerer Fläche schwenkt ein Finger – für einen Rahmen „Mehrfach“ einschalten.'
          : toolDef.hint;
      case 'arrow':
        return current.points
          ? 'Knickpunkte und Enden an den weißen Punkten ziehen; doppelt auf die Linie tippen setzt einen neuen Knick. Das Element selbst ziehen verschiebt es.'
          : 'Anfang und Ende an den weißen Punkten ziehen, der blaue Punkt in der Mitte biegt den Pfeil. Den Pfeil selbst ziehen verschiebt ihn.';
      case 'measure':
        return 'Die Enden an den weißen Punkten ziehen; die Maßlinie selbst ziehen verschiebt sie.';
      case 'zone':
        return 'Die Eckpunkte ziehen ändert die Größe; die Zone selbst ziehen verschiebt sie.';
      case 'player':
        return 'Ziehen verschiebt den Spieler; der Griff am Ring dreht die Blickrichtung.';
      default:
        return 'Ziehen verschiebt das Element; die Eigenschaften stehen unter dem Feld in der Auswahl.';
    }
  };

  let hint = toolDef.hint;
  if (tool === 'select') {
    if (rowActive && row.direction === 'target') hint = 'Tippe aufs Feld, wohin die Reihe reichen soll – ziehen rückt den Punkt zurecht.';
    else if (multi) hint = 'Mehrfachauswahl: Elemente antippen, um sie hinzuzufügen oder herauszunehmen – oder einen Rahmen aufziehen.';
    else hint = overlapNext ? `Nochmal tippen: ${overlapNext}. ${selectHint()}` : selectHint();
  }
  const extending = extend !== null ? elements[extend] : undefined;
  const knickCount = extending?.type === 'arrow' ? (extending.points?.length ?? 0) : 0;
  // Wo die Auswahl steht, haengt vom Aufbau ab: im normalen Editor unter dem Feld, im breiten Vollbild rechts, am Handy im Vollbild unten.
  const selectionPlace = expanded ? (wide ? 'rechts' : 'unten') : 'unter dem Feld';
  const statusRaw =
    notice ??
    (anchor
      ? 'Anfang gesetzt – jetzt das Ende antippen.'
      : extend !== null && extending?.type === 'arrow'
        ? `Weiter tippen setzt Knickpunkte (${knickCount} von ${DIAGRAM_ARROW_MAX_POINTS}). Ein Tipp auf den letzten Punkt oder „Fertig“ beendet den Pfeil.`
        : hint);
  const status = statusRaw.replace(/unter dem Feld/g, selectionPlace);

  // Breite der freien Flaeche, wie bei der Masslinie eingetragen (sonst die Annahme des Editors): fuer die Rasterweite in Metern.
  const areaWidthMeters = /^\d{1,3}$/.test(areaWidth.trim()) && Number(areaWidth.trim()) > 0 ? Number(areaWidth.trim()) : AREA_WIDTH_DEFAULT_METERS;
  const gridInfo = grid ? gridWidthText(pitch, viewport.zoom, areaWidthMeters) : null;

  const guideColor = '#f472b6';
  const guideDash = `${hitSize(2, viewport.zoom)} ${hitSize(1.4, viewport.zoom)}`;
  // Am Handy bleibt das Feld bei einer Auswahl oben stehen - ausser die Bedienungshilfe ist offen (sie ist lang und gehoert nicht in die feste Buehne).
  const pinned = selection.length > 0 && !helpOpen;
  const activeName = tool === 'select' && multi ? 'Auswählen (Mehrfach)' : toolDef.name;

  const transformSlot = transformOpen ? <TransformPanel id={transformId} whole={selection.length === 0} disabled={disabled} onTransform={applyTransform} /> : null;

  /* Seitenverhaeltnis der Zeichnung: das Feld wird nie breiter als hoch erlaubt, sodass es keinen leeren Rand gibt, den ein Tipp faelschlich als Feldrand deutet. */
  const ratioBox = drawingBox(pitch);
  const ratio = ratioBox.w / ratioBox.h;
  const fit = expanded ? fitBox(stageSize, ratio) : null;
  // Normal: so breit wie der Platz, aber hoechstens so hoch, dass Leiste und Feld in den sichtbaren Bereich passen (am Handy mit Auswahl hoechstens 30 % der Hoehe).
  const surfaceClass = cn(
    'rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2',
    expanded ? cn('shrink-0', !fit && 'h-full w-full') : cn('mx-auto w-full [--field-h:max(14rem,calc(92dvh-20.5rem))]', pinned && 'max-sm:[--field-h:30dvh]'),
  );
  const surfaceStyle = expanded ? (fit ? { width: fit.width, height: fit.height } : undefined) : { width: `min(100%, calc(var(--field-h) * ${ratio.toFixed(4)}))` };

  const toolPanel = (
    <ToolPanel
      tool={tool}
      onTool={chooseTool}
      multi={multi}
      onToggleMulti={toggleMulti}
      disabled={disabled}
      onInsertSetup={() => setInsertOpen(true)}
      onInsertFormation={() => setFormationOpen(true)}
      orientation={expanded && wide ? 'vertical' : 'horizontal'}
    />
  );

  const field = (
    <div
      ref={surfaceRef}
      tabIndex={disabled ? -1 : 0}
      role="group"
      aria-label="Zeichenfläche der Skizze"
      aria-describedby={hintId}
      data-testid="diagram-surface"
      onKeyDown={onKeyDown}
      className={surfaceClass}
  style={surfaceStyle}
    >
      <svg
        ref={viewport.svgRef}
        role="img"
        aria-label={describeDiagram({ pitch, elements })}
        viewBox={viewport.viewBox}
        data-zoom={viewport.zoomed ? viewport.zoom : undefined}
        data-testid="diagram-canvas"
        data-pitch={pitch}
        className={cn('block w-full select-none', expanded ? 'h-full' : cn('h-auto max-h-[var(--field-h)]', pinned && 'max-sm:max-h-[30vh]'))}
        // Ziehen auf dem Feld darf die Seite nicht scrollen oder zoomen: das machen die Zwei-Finger-Geste und die Knoepfe.
        style={{ touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onContextMenu={(event) => event.preventDefault()}
      >
        <PitchSurface pitch={pitch} markerId={markerId} scale={symbolScale} zoneColors={zoneColorsOf(ghost ? [...elements, ghost] : elements)} />
        {grid && <DiagramGrid pitch={pitch} zoom={viewport.zoom} />}
        {ordered.map(({ element, index }) => (
          <g key={index} data-index={index} data-type={element.type}>
            <SketchElement element={element} markerId={markerId} height={height} scale={symbolScale} />
            <HitShape element={element} height={height} zoom={viewport.zoom} scale={symbolScale} />
          </g>
        ))}
        {ghost && (
          <g opacity={0.75} pointerEvents="none" data-testid="diagram-draft">
            <SketchElement element={ghost} markerId={markerId} height={height} scale={symbolScale} />
          </g>
        )}
        {anchor && (
          <circle
            data-testid="diagram-anchor"
            cx={anchor.x}
            cy={(anchor.y / 100) * height}
            r={hitSize(2, viewport.zoom)}
            fill={ARROW}
            stroke="#ffffff"
            strokeWidth={hitSize(0.6, viewport.zoom)}
            pointerEvents="none"
          />
        )}
        {showSteps && <StepNumbers diagram={{ pitch, elements }} height={height} />}
        {rowPlan && rowPlan.problem === null && (
          <g opacity={0.65} pointerEvents="none" data-testid="diagram-row-preview">
            {rowPlan.copies.map((element, index) => (
              <SketchElement key={index} element={element} markerId={markerId} height={height} scale={symbolScale} />
            ))}
          </g>
        )}
        {rowActive && row.direction === 'target' && row.target && (
          <circle
            data-testid="diagram-row-target"
            cx={row.target.x}
            cy={(row.target.y / 100) * height}
            r={hitSize(2, viewport.zoom)}
            fill={SELECT_COLOR}
            stroke="#ffffff"
            strokeWidth={hitSize(0.6, viewport.zoom)}
            pointerEvents="none"
          />
        )}
        {!marquee && current && <SelectionMark element={current} height={height} zoom={viewport.zoom} scale={symbolScale} activeKnick={activeKnick} />}
        {(marquee || selection.length > 1) &&
          shown.map((index) => elements[index] && <SelectionMark key={index} element={elements[index]!} height={height} zoom={viewport.zoom} scale={symbolScale} group />)}
        {pickingReference && referenceIndex !== null && elements[referenceIndex] && (
          <ReferenceMark element={elements[referenceIndex]!} height={height} zoom={viewport.zoom} scale={symbolScale} />
        )}
        {guides.x.map((x) => (
          <line
            key={`x${x}`}
            data-testid="diagram-guide"
            data-axis="x"
            x1={x}
            x2={x}
            y1={0}
            y2={height}
            stroke={guideColor}
            strokeWidth={hitSize(0.35, viewport.zoom)}
            strokeDasharray={guideDash}
            pointerEvents="none"
          />
        ))}
        {guides.y.map((y) => (
          <line
            key={`y${y}`}
            data-testid="diagram-guide"
            data-axis="y"
            x1={0}
            x2={100}
            y1={(y / 100) * height}
            y2={(y / 100) * height}
            stroke={guideColor}
            strokeWidth={hitSize(0.35, viewport.zoom)}
            strokeDasharray={guideDash}
            pointerEvents="none"
          />
        ))}
        {marquee && (
          <rect
            data-testid="diagram-marquee"
            x={Math.min(marquee.start.x, marquee.current.x)}
            y={(Math.min(marquee.start.y, marquee.current.y) / 100) * height}
            width={Math.abs(marquee.current.x - marquee.start.x)}
            height={(Math.abs(marquee.current.y - marquee.start.y) / 100) * height}
            fill={SELECT_COLOR}
            fillOpacity={0.14}
            stroke={SELECT_COLOR}
            strokeWidth={hitSize(0.7, viewport.zoom)}
            strokeDasharray={guideDash}
            pointerEvents="none"
          />
        )}
      </svg>
    </div>
  );

  /* Die feste Stelle fuer alles, was ein Tipp oder Ziehen gerade bewirkt: das aktive Werkzeug, der Hinweis und Rueckmeldungen. */
  const statusBlock = (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <p data-testid="active-tool" className="text-xs font-semibold text-slate-800">
          Werkzeug: {activeName}
        </p>
        <p className={cn('text-xs tabular-nums', full ? 'font-medium text-amber-700' : 'text-slate-500')}>
          {elements.length} von {DIAGRAM_MAX_ELEMENTS} Elementen
        </p>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <p id={hintId} role="status" className={cn('text-xs', notice ? 'font-medium text-amber-700' : 'text-slate-600')}>
          {status}
        </p>
        {extend !== null && extending?.type === 'arrow' && (
          <Button type="button" size="sm" onClick={() => setExtend(null)}>
            Fertig
          </Button>
        )}
      </div>
    </div>
  );

  const zoneOptions = tool === 'zone' && (
    <ZoneOptions look={zoneLook} disabled={disabled} onChange={(patch) => setZoneLook((state) => ({ ...state, ...patch }))} />
  );

  const viewBar = (panel: boolean): JSX.Element => (
    <ViewBar
      panel={panel}
      showZoom={!(panel && wide)}
      viewport={viewport}
      pitch={pitch}
      symbolScale={symbolScale}
      snap={snap}
      grid={grid}
      steps={showSteps}
      gridInfo={
        grid ? (
          <p data-testid="grid-info" className="text-xs text-slate-500">
            Raster: kräftige Linien alle 5 Einheiten{viewport.zoom >= 2 - 1e-6 ? ', feine jede Einheit' : ''}; Rasterweite {gridStepFor(viewport.zoom) === 1 ? '1 Einheit' : '0,5 Einheiten'}
            {gridInfo ? ` (≈ ${gridInfo})` : ''}.
          </p>
        ) : null
      }
      hasArrows={hasArrows}
      disabled={disabled}
      onPitch={changePitch}
      onSymbolScale={changeSymbolScale}
      onToggleSnap={() => setSnap((on) => !on)}
      onToggleGrid={() => setGrid((on) => !on)}
      onToggleSteps={() => setShowSteps((on) => !on)}
      onPreview={() => setPreviewing(true)}
    />
  );

  const selectionArea = selection.length > 0 ? (
    <SelectionPanel
      elements={elements}
      selection={selection}
      disabled={disabled}
      rowOpen={rowActive}
      rowPanelId={rowPanelId}
      properties={
        selection.length === 1 && current ? (
          <Properties
            element={current}
            disabled={disabled}
            onChange={updateSelected}
            index={selected}
            steps={steps}
            pitch={pitch}
            areaWidth={areaWidth}
            onAreaWidth={setAreaWidth}
            activeKnick={activeKnick}
            onActiveKnick={setActiveKnick}
            focusText={focusText}
            stepsHidden={!showSteps}
          />
        ) : undefined
      }
      overlapSlot={
        overlap ? <OverlapList names={overlapNames} indices={overlap.indices} selected={selected} disabled={disabled} onPick={pickFromPile} /> : undefined
      }
      onOrder={orderSelection}
      transformSlot={transformSlot}
      rowSlot={
        rowActive && rowPlan ? (
          <RowPanel
            id={rowPanelId}
            input={row}
            problem={rowPlan.problem}
            count={rowPlan.copies.length}
            disabled={disabled}
            onChange={(patch) => setRow((state) => ({ ...state, ...patch, ...(patch.direction ? { target: null } : {}) }))}
            onApply={applyRow}
            onCancel={() => setRow((state) => ({ ...state, open: false, target: null }))}
          />
        ) : null
      }
      onDuplicate={duplicateSelection}
      onRemove={removeSelected}
      onToggleSmall={toggleSmallSelection}
      onTeam={changeSelectionTeam}
      onFacing={changeSelectionFacing}
      onAlign={alignSelection}
      onDistribute={distributeSelection}
      alignOpen={alignOpen}
      onToggleAlign={() => setAlignOpen((open) => !open)}
      centerMode={centerMode}
      onCenterMode={(mode) => {
        setCenterMode(mode);
        setNotice(null);
      }}
      onCenter={centerSelection}
      referenceSet={referenceIndex !== null}
      onToggleRow={toggleRow}
      transformOpen={transformOpen}
      transformId={transformId}
      onToggleTransform={() => setTransformOpen((open) => !open)}
      onSaveBlock={openSaveBlock}
      onClear={() => setSelection([])}
    />
  ) : (
    <EmptySelection
      disabled={disabled}
      empty={elements.length === 0}
      transformOpen={transformOpen}
      transformId={transformId}
      transformSlot={transformSlot}
      onSelectAll={selectAll}
      onToggleTransform={() => setTransformOpen((open) => !open)}
      onClearAll={clearAll}
      onRemoveDiagram={removeDiagram}
    />
  );

  const dialogs = (
    <>
      {formationOpen && <FormationPicker pitch={pitch} used={elements.length} onInsert={insertFormation} onClose={() => setFormationOpen(false)} />}

      {insertOpen && (
        <DiagramBlockPicker
          pitch={pitch}
          existing={elements}
          at={lastTap.current}
          areaWidthMeters={areaWidthMeters}
          onInsert={insertBlock}
          onClose={() => setInsertOpen(false)}
        />
      )}
      {blockDraft && <SaveBlockDialog content={blockDraft} onClose={() => setBlockDraft(null)} />}
    </>
  );

  /* ----------------------------------------------------------- Vollbild */

  if (expanded) {
    const picked = selection.map((index) => elements[index]).filter((element): element is DiagramElement => element !== undefined);
    const sheetLabel = picked.length > 0 ? selectionTitle(picked) : tool === 'zone' ? 'Form und Farbe der Zone' : 'Ganze Skizze';
    const side = (
      <>
        {zoneOptions}
        {selectionArea}
      </>
    );
    const popover = helpOpen ? <DiagramHelp id={helpId} onClose={() => setHelpOpen(false)} /> : viewOpen ? viewBar(true) : null;
    return (
      <>
        <ExpandedShell
          wide={wide}
          title="Skizze groß bearbeiten"
          onClose={() => setExpanded(false)}
          history={
            <div role="toolbar" aria-label="Rückgängig und Wiederherstellen" data-testid="history-bar" className="flex items-center gap-1.5">
              <HeaderButton label="Rückgängig" icon="undo" showText={wide} disabled={disabled || undoDepth === 0} onClick={undo} />
              <HeaderButton label="Wiederherstellen" icon="redo" showText={wide} disabled={disabled || redoDepth === 0} onClick={redo} />
            </div>
          }
          zoom={<ZoomControls viewport={viewport} />}
          menu={
            <>
              <HeaderButton
                label="Ansicht"
                icon="view"
                showText={wide}
                expanded={viewOpen}
                onClick={() => {
                  setViewOpen((open) => !open);
                  setHelpOpen(false);
                }}
              />
              <HeaderButton
                label="Bedienung"
                icon="help"
                showText={wide}
                expanded={helpOpen}
                controls={helpId}
                onClick={() => {
                  setHelpOpen((open) => !open);
                  setViewOpen(false);
                }}
              />
            </>
          }
          popover={popover}
          toolbar={toolPanel}
          field={field}
          fieldRef={stageRef}
          status={statusBlock}
          side={side}
          sheetLabel={sheetLabel}
          sheetOpen={sheetOpen}
          onToggleSheet={() => setSheetOpen((open) => !open)}
          sheetId={sheetId}
        />
        {dialogs}
      </>
    );
  }

  return (
    // Am Handy steht das Feld bei einer Auswahl oben fest: Knöpfe und Felder sollen beim Hinscrollen (Fokus, Tabulator) darunter landen, nicht dahinter.
    <div className={cn('space-y-3 max-sm:[&_button]:scroll-mt-[52vh] max-sm:[&_input]:scroll-mt-[52vh] max-sm:[&_select]:scroll-mt-[52vh]', flush && 'max-sm:px-3')}>
      {wide ? (
        // Breit: Rueckgaengig, Wiederherstellen, Bedienung und "Gross bearbeiten" stehen als Symbolknoepfe rechts in der Zeile der Werkzeugleiste, die beim Scrollen unter der Reiterleiste klebt.
        <div data-testid="tool-row" className="sticky top-[var(--tab-bar-h,0px)] z-20 -mx-1 flex items-start gap-2 bg-slate-50 px-1 pt-1">
          <div className="min-w-0 flex-1">{toolPanel}</div>
          <div className="flex shrink-0 items-center gap-1.5">
            <div role="toolbar" aria-label="Rückgängig und Wiederherstellen" data-testid="history-bar" className="flex items-center gap-1.5">
              <HeaderButton label="Rückgängig" icon="undo" showText={false} disabled={disabled || undoDepth === 0} onClick={undo} />
              <HeaderButton label="Wiederherstellen" icon="redo" showText={false} disabled={disabled || redoDepth === 0} onClick={redo} />
            </div>
            <HeaderButton label="Bedienung" icon="help" showText={false} expanded={helpOpen} controls={helpId} onClick={() => setHelpOpen((open) => !open)} />
            <ExpandButton buttonRef={expandRef} iconOnly onClick={() => setExpanded(true)} />
          </div>
        </div>
      ) : (
        toolPanel
      )}

      {/* Bei einer Auswahl bleibt das Feld samt Hinweiszeile am Handy oben stehen (der Bereich "Auswahl" darunter ist lang): so sieht man, was ein Knopf am Feld bewirkt. Im Reiterformular klebt es unter der Reiterleiste (`--tab-bar-h`), nicht auf ihr. */}
      <div
        data-testid="diagram-stage"
        data-pinned={pinned ? 'true' : undefined}
        className={cn('space-y-2', pinned && 'max-sm:sticky max-sm:top-[var(--tab-bar-h,0px)] max-sm:z-20 max-sm:-mx-3 max-sm:space-y-1 max-sm:bg-slate-50 max-sm:px-3 max-sm:pb-1.5 max-sm:pt-1 max-sm:shadow-[0_8px_8px_-8px_rgba(15,23,42,0.45)]')}
      >
        {!wide && (
          <>
            {/* Rueckgaengig und Wiederherstellen stehen immer hier, direkt ueber dem Feld; rechts die Bedienungshilfe und "Gross bearbeiten". */}
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div role="toolbar" aria-label="Rückgängig und Wiederherstellen" data-testid="history-bar" className="flex items-center gap-1.5">
                <ToolButton label="Rückgängig" short="Rückgängig" icon="undo" layout="inline" disabled={disabled || undoDepth === 0} onClick={undo} />
                <ToolButton label="Wiederherstellen" short="Wiederherstellen" icon="redo" layout="inline" disabled={disabled || redoDepth === 0} onClick={redo} />
              </div>
              <div className="flex items-center gap-1.5">
                <ToolButton label="Bedienung" short="Bedienung" icon="help" layout="inline" expanded={helpOpen} controls={helpId} onClick={() => setHelpOpen((open) => !open)} />
                <ExpandButton buttonRef={expandRef} onClick={() => setExpanded(true)} />
              </div>
            </div>
          </>
        )}

        {helpOpen && (
          <div className="w-full">
            <DiagramHelp id={helpId} onClose={() => setHelpOpen(false)} />
          </div>
        )}

        {field}

        {statusBlock}
      </div>

      {zoneOptions}

      {viewBar(false)}

      {selectionArea}

      {dialogs}
    </div>
  );

}

/* ------------------------------------------------------------ Eigenschaften */

function Properties({
  element,
  index,
  disabled,
  onChange,
  steps,
  pitch,
  areaWidth,
  onAreaWidth,
  activeKnick,
  onActiveKnick,
  focusText,
  stepsHidden = false,
}: {
  element: DiagramElement;
  index: number | null;
  steps: DiagramStep[];
  pitch: DiagramPitch;
  /** Breite der freien Flaeche in Metern (Text wie eingegeben); nur fuer den Vorschlag "Massstab". */
  areaWidth: string;
  onAreaWidth: (value: string) => void;
  /** Zuletzt angefasster Knickpunkt des markierten Pfeils; "Knick entfernen" nimmt ihn (sonst den letzten). */
  activeKnick: number | null;
  onActiveKnick: (knick: number | null) => void;
  /** Aendert sich, wenn das Textfeld den Fokus nehmen soll (neuer Text, Doppeltipp auf einen Text). */
  focusText: number;
  /** Die Schrittnummern sind im Editor ausgeblendet (Ansicht): ein Hinweis bei der Eigenschaft "Schritt", die trotzdem gilt. */
  stepsHidden?: boolean;
  disabled?: boolean;
  onChange: (change: (element: DiagramElement) => DiagramElement, key?: string | null) => void;
}): JSX.Element {
  const position = element.type === 'arrow' && index !== null ? steps.find((step) => step.arrows.includes(index))?.number : undefined;

  return (
    <PanelGroup title="Eigenschaften" testId="diagram-properties">

      {element.type === 'player' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Mannschaft">
            {(id) => (
              <Select
                id={id}
                value={element.team}
                disabled={disabled}
                onChange={(event) => {
                  const team = event.target.value as DiagramTeam;
                  onChange((entry) => (entry.type === 'player' ? { ...entry, team } : entry));
                }}
              >
                {TEAM_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Beschriftung" hint="Höchstens 4 Zeichen, z. B. Nummer oder „TW“.">
            {(id) => (
              <Input
                id={id}
                value={element.label ?? ''}
                maxLength={4}
                autoComplete="off"
                disabled={disabled}
                // Enter darf das umgebende Formular nicht abschicken.
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.preventDefault();
                }}
                onChange={(event) => {
                  const label = event.target.value.replace(/\s/g, '').slice(0, 4);
                  onChange((entry) => {
                    if (entry.type !== 'player') return entry;
                    const { label: _alt, ...rest } = entry;
                    return label ? { ...rest, label } : rest;
                  }, `label-${index}`);
                }}
              />
            )}
          </Field>
          <FacingProperties element={element} disabled={disabled} onChange={onChange} />
        </div>
      )}

      {element.type === 'text' && <TextProperties element={element} index={index} disabled={disabled} focusToken={focusText} onChange={onChange} />}

      {element.type === 'zone' && <ZoneProperties element={element} disabled={disabled} onChange={onChange} />}

      {element.type === 'goal' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Größe">
            {(id) => (
              <Select
                id={id}
                value={element.size}
                disabled={disabled}
                onChange={(event) => {
                  const size = event.target.value as (typeof DIAGRAM_GOAL_SIZE)[number];
                  onChange((entry) => (entry.type === 'goal' ? { ...entry, size } : entry));
                }}
              >
                {DIAGRAM_GOAL_SIZE.map((size) => (
                  <option key={size} value={size}>
                    {GOAL_SIZE_LABELS[size]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Drehung">
            {(id) => (
              <Select
                id={id}
                value={String(element.rotation ?? 0)}
                disabled={disabled}
                onChange={(event) => {
                  const rotation = Number(event.target.value) as 0 | 90 | 180 | 270;
                  onChange((entry) => (entry.type === 'goal' ? { ...entry, rotation } : entry));
                }}
              >
                {[0, 90, 180, 270].map((degrees) => (
                  <option key={degrees} value={degrees}>
                    {degrees}°
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      )}

      {element.type === 'hurdle' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Drehung" hint="Quer zur Laufrichtung stellen: 0° und 90° gerade, 45° und 135° schräg.">
            {(id) => (
              <Select
                id={id}
                value={String(element.rotation ?? 0)}
                disabled={disabled}
                onChange={(event) => {
                  const rotation = Number(event.target.value) as DiagramHurdleRotation;
                  onChange((entry) => (entry.type === 'hurdle' ? { ...entry, rotation } : entry));
                }}
              >
                {DIAGRAM_HURDLE_ROTATION.map((degrees) => (
                  <option key={degrees} value={degrees}>
                    {degrees}°
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      )}

      {element.type === 'arrow' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Art" hint="Anfang und Ende lassen sich an den weißen Punkten ziehen.">
            {(id) => (
              <Select
                id={id}
                value={element.kind}
                disabled={disabled}
                onChange={(event) => {
                  const kind = event.target.value as DiagramArrowKind;
                  onChange((entry) => {
                    if (entry.type !== 'arrow') return entry;
                    // Der hohe Ball hat keine Knickpunkte: sie entfallen (der Pfeil wird gerade; biegen geht weiter).
                    const { points: _points, smooth: _smooth, ...rest } = entry;
                    // "Hinwechseln" gibt es nur beim Pass: bei jeder anderen Art entfaellt das Feld.
                    const next = kind === 'LOFT' ? { ...rest, kind } : { ...entry, kind };
                    // "Laeuft bis Schritt" gibt es nur bei Laufweg, Dribbling und Positionswechsel.
                    const spanned = canSpanSteps(kind) ? next : withStepEnd(next, undefined);
                    if (kind === 'PASS') return spanned;
                    const { follow: _follow, ...without } = spanned;
                    return without;
                  });
                }}
              >
                {DIAGRAM_ARROW_KIND.map((kind) => (
                  <option key={kind} value={kind}>
                    {ARROW_LABELS[kind]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field
            label="Schritt"
            hint={`Leer = automatisch${position ? ` (zurzeit Schritt ${position} von ${steps.length})` : ''}. Gleiche Nummer = gleichzeitig.${stepsHidden ? ' Die Schrittnummern sind im Feld ausgeblendet (Ansicht).' : ''}`}
          >
            {(id) => (
              <Input
                id={id}
                value={element.step ?? ''}
                inputMode="numeric"
                maxLength={2}
                placeholder="automatisch"
                autoComplete="off"
                disabled={disabled}
                // Enter darf das umgebende Formular nicht abschicken.
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.preventDefault();
                }}
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
                  const step = digits === '' ? undefined : Math.min(Number(digits), DIAGRAM_MAX_STEP);
                  // Null ist keine Schrittnummer - die Eingabe bleibt, wie sie war.
                  if (step === 0) return;
                  onChange((entry) => (entry.type === 'arrow' ? withStep(entry, step) : entry), `step-${index}`);
                }}
              />
            )}
          </Field>
          {canSpanSteps(element.kind) && (
            <Field
              label="Läuft bis Schritt"
              hint={`Der Spieler läuft ohne Halt über mehrere Schritte – so wirkt der Ablauf flüssiger.${element.step === undefined ? ' Zuerst eine Schrittnummer eintragen.' : ''}`}
            >
              {(id) => (
                <Select
                  id={id}
                  value={element.stepEnd === undefined ? '' : String(element.stepEnd)}
                  disabled={disabled || element.step === undefined}
                  onChange={(event) => {
                    const end = event.target.value === '' ? undefined : Number(event.target.value);
                    onChange((entry) => (entry.type === 'arrow' ? withStepEnd(entry, end) : entry));
                  }}
                >
                  <option value="">nur dieser Schritt</option>
                  {Array.from({ length: Math.max(0, DIAGRAM_MAX_STEP - (element.step ?? DIAGRAM_MAX_STEP)) }, (_, offset) => (element.step ?? 0) + 1 + offset).map((number) => (
                    <option key={number} value={String(number)}>
                      Schritt {number}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {element.kind === 'PASS' && (
            <div role="group" aria-label="Hinwechseln" className="flex flex-wrap items-center gap-2 sm:col-span-2">
              <Button
                type="button"
                size="sm"
                variant={element.follow ? 'primary' : 'secondary'}
                aria-pressed={element.follow === true}
                disabled={disabled}
                onClick={() =>
                  onChange((entry) => {
                    if (entry.type !== 'arrow' || entry.kind !== 'PASS') return entry;
                    const { follow: _alt, ...rest } = entry;
                    return entry.follow ? rest : { ...rest, follow: true };
                  })
                }
              >
                Hinwechseln
              </Button>
              <span className="text-xs text-slate-500">Der Passgeber läuft dem Pass nach und übernimmt die Position am Ziel.</span>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 sm:col-span-2">
            {element.points ? (
              <p className="text-xs text-slate-500">
                Knickpunkte (weiße Punkte) ziehen; doppelt antippen entfernt einen, doppelt auf die Linie antippen setzt einen neuen.
              </p>
            ) : (
              <p className="text-xs text-slate-500">Biegen: den blauen Punkt in der Mitte ziehen. Doppelt antippen macht den Pfeil wieder gerade.</p>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled || (!element.via && !element.points)}
              onClick={() => onChange((entry) => (entry.type === 'arrow' ? straightenArrow(entry) : entry))}
            >
              Gerade machen
            </Button>
          </div>
          {element.points && (
            <div role="group" aria-label="Verlauf" className="flex flex-wrap items-center gap-2 sm:col-span-2">
              <span className="text-sm font-medium text-slate-700">Verlauf</span>
              <Button
                type="button"
                size="sm"
                variant={element.smooth ? 'primary' : 'secondary'}
                aria-pressed={element.smooth === true}
                disabled={disabled}
                onClick={() => onChange((entry) => (entry.type === 'arrow' ? setSmooth(entry, true) : entry))}
              >
                Weich
              </Button>
              <Button
                type="button"
                size="sm"
                variant={element.smooth ? 'secondary' : 'primary'}
                aria-pressed={element.smooth !== true}
                disabled={disabled}
                onClick={() => onChange((entry) => (entry.type === 'arrow' ? setSmooth(entry, false) : entry))}
              >
                Eckig
              </Button>
              <span className="text-xs text-slate-500">Weich: glatte Kurve durch alle Punkte (Slalom, Dribbling). Eckig: gerade Strecken, Ecken leicht gerundet.</span>
            </div>
          )}
          <div role="group" aria-label="Knickpunkte" className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled || !canAddKnick(element)}
              title={element.kind === 'LOFT' ? 'Ein hoher Ball hat keine Knickpunkte' : `Höchstens ${DIAGRAM_ARROW_MAX_POINTS} Knickpunkte`}
              onClick={() => onChange((entry) => (entry.type === 'arrow' ? (addKnickAtLongest(entry, pitch) ?? entry) : entry))}
            >
              Knick hinzufügen
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled || !element.points}
              onClick={() => {
                onChange((entry) => (entry.type === 'arrow' ? removeKnick(entry, activeKnick ?? undefined) : entry));
                onActiveKnick(null);
              }}
            >
              Knick entfernen
            </Button>
            <span className="text-xs text-slate-500">
              {element.kind === 'LOFT' ? 'Hoher Ball: biegen ja, Knickpunkte nein.' : `${element.points?.length ?? 0} von ${DIAGRAM_ARROW_MAX_POINTS} Knickpunkten`}
            </span>
          </div>
        </div>
      )}

      {element.type === 'measure' && (
        <MeasureProperties element={element} index={index} pitch={pitch} areaWidth={areaWidth} onAreaWidth={onAreaWidth} disabled={disabled} onChange={onChange} />
      )}

      {element.type !== 'goal' && element.type !== 'zone' && element.type !== 'arrow' && element.type !== 'measure' && (
        <div className="flex min-h-11 items-center">
          <Checkbox
            label="Klein"
            checked={element.size === 'S'}
            disabled={disabled}
            onChange={(small) =>
              onChange((entry) => {
                if (entry.type === 'goal' || entry.type === 'zone' || entry.type === 'arrow' || entry.type === 'measure') return entry;
                const { size: _alt, ...rest } = entry;
                return small ? { ...rest, size: 'S' } : rest;
              })
            }
          />
        </div>
      )}

      {(element.type === 'cone' || element.type === 'pole' || element.type === 'ring' || element.type === 'ball') && (
        <p className="text-xs text-slate-500">Dieses Element hat keine weiteren Eigenschaften – verschieben oder löschen.</p>
      )}
    </PanelGroup>
  );
}

const ZONE_ROTATION_LABELS: Record<DiagramZoneRotation, string> = { 0: 'oben', 90: 'rechts', 180: 'unten', 270: 'links' };

/** Form, Farbe und (beim Dreieck) Spitze einer Zone - Auswahlfelder, die in den Eigenschaften und beim Werkzeug "Zone" gleich aussehen. */
function ZoneLookFields({
  look,
  disabled,
  prefix,
  onChange,
}: {
  look: ZoneLook;
  disabled?: boolean;
  /** Vorsatz der Bezeichnungen ("Neue Zone: "), damit beide Bereiche gleichzeitig auf dem Bildschirm eindeutig bleiben. */
  prefix: string;
  onChange: (patch: Partial<ZoneLook>) => void;
}): JSX.Element {
  return (
    <>
      <Field label={`${prefix}Form`}>
        {(id) => (
          <Select id={id} value={look.shape} disabled={disabled} onChange={(event) => onChange({ shape: event.target.value as DiagramZoneShape })}>
            {DIAGRAM_ZONE_SHAPE.map((shape) => (
              <option key={shape} value={shape}>
                {DIAGRAM_ZONE_SHAPE_LABELS[shape]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label={`${prefix}Farbe`}>
        {(id) => (
          <Select id={id} value={look.color} disabled={disabled} onChange={(event) => onChange({ color: event.target.value as DiagramZoneColor })}>
            {DIAGRAM_ZONE_COLOR.map((color) => (
              <option key={color} value={color}>
                {DIAGRAM_ZONE_COLOR_LABELS[color]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {look.shape === 'triangle' && (
        <Field label={`${prefix}Spitze`}>
          {(id) => (
            <Select id={id} value={String(look.rotation)} disabled={disabled} onChange={(event) => onChange({ rotation: Number(event.target.value) as DiagramZoneRotation })}>
              {DIAGRAM_ZONE_ROTATION.map((rotation) => (
                <option key={rotation} value={rotation}>
                  {ZONE_ROTATION_LABELS[rotation]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </>
  );
}

/** Einstellungen fuer neue Zonen: stehen unter der Zeichenflaeche, solange das Werkzeug "Zone" aktiv ist. */
function ZoneOptions({ look, disabled, onChange }: { look: ZoneLook; disabled?: boolean; onChange: (patch: Partial<ZoneLook>) => void }): JSX.Element {
  return (
    <section aria-label="Form und Farbe neuer Zonen" data-testid="zone-options" className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-white p-2 sm:grid-cols-3">
      <ZoneLookFields look={look} disabled={disabled} prefix="Neue Zone: " onChange={onChange} />
    </section>
  );
}

/** Eigenschaften einer markierten Zone: Form, Farbe, Spitze des Dreiecks; die Groesse aendern die Eckgriffe. */
function ZoneProperties({
  element,
  disabled,
  onChange,
}: {
  element: Extract<DiagramElement, { type: 'zone' }>;
  disabled?: boolean;
  onChange: (change: (element: DiagramElement) => DiagramElement, key?: string | null) => void;
}): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      <ZoneLookFields
        look={zoneLookOf(element)}
        disabled={disabled}
        prefix=""
        onChange={(patch) => onChange((entry) => (entry.type === 'zone' ? applyZoneLook(entry, { ...zoneLookOf(entry), ...patch }) : entry))}
      />
      <p className="col-span-2 text-xs text-slate-500 sm:col-span-3">
        Größe ändern: die vier Eckpunkte ziehen, mindestens {ZONE_MIN_SIZE} Prozent je Seite. Verschieben: die Zone selbst ziehen.
      </p>
    </div>
  );
}

/** Eingabefeld fuer einen Text auf dem Feld; leer geht nicht (der Wortlaut bleibt dann, wie er war). */
function TextProperties({
  element,
  index,
  disabled,
  focusToken,
  onChange,
}: {
  element: Extract<DiagramElement, { type: 'text' }>;
  index: number | null;
  disabled?: boolean;
  focusToken: number;
  onChange: (change: (element: DiagramElement) => DiagramElement, key?: string | null) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(element.text);
  const seen = useRef(focusToken);
  // Aenderungen von aussen (Rueckgaengig, anderes Element) ueberschreiben das Eingabefeld.
  useEffect(() => setDraft(element.text), [element.text, index]);
  // Ein neuer Wert des Zaehlers: Fokus ins Feld, alles markiert - Tippen ersetzt den Platzhalter.
  useEffect(() => {
    if (focusToken === seen.current) return;
    seen.current = focusToken;
    // `Input` reicht keinen ref weiter: das Feld wird ueber sein Merkmal gefunden (es gibt immer nur eines).
    const field = document.querySelector<HTMLInputElement>('input[data-text-field]');
    field?.focus({ preventScroll: true });
    field?.select();
  }, [focusToken]);
  const empty = draft.trim() === '';

  return (
    <div className="space-y-1">
      <Field label="Text" hint={`Eine Zeile, höchstens ${DIAGRAM_TEXT_MAX} Zeichen. Er bleibt im Feld, auch am Rand.`}>
        {(id) => (
          <Input
            id={id}
            data-text-field=""
            value={draft}
            maxLength={DIAGRAM_TEXT_MAX}
            autoComplete="off"
            disabled={disabled}
            aria-invalid={empty || undefined}
            // Enter darf das umgebende Formular nicht abschicken; es beendet die Eingabe.
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.blur();
              }
            }}
            onBlur={() => {
              if (empty) setDraft(element.text);
            }}
            onChange={(event) => {
              const text = event.target.value.replace(/[\r\n]+/g, ' ').replace(/^\s+/, '').slice(0, DIAGRAM_TEXT_MAX);
              setDraft(text);
              if (text.trim() === '') return;
              onChange((entry) => (entry.type === 'text' ? { ...entry, text } : entry), `text-${index}`);
            }}
          />
        )}
      </Field>
      {empty && (
        <p role="alert" className="text-xs font-medium text-amber-700">
          Der Text darf nicht leer sein – sonst „Element löschen“ nehmen.
        </p>
      )}
    </div>
  );
}

/** Blickrichtung eines Spielers: acht Richtungsknoepfe, "Keine" und ein Regler fuer den genauen Winkel (der Griff im Feld dreht ebenfalls). */
function FacingProperties({
  element,
  disabled,
  onChange,
}: {
  element: Extract<DiagramElement, { type: 'player' }>;
  disabled?: boolean;
  onChange: (change: (element: DiagramElement) => DiagramElement, key?: string | null) => void;
}): JSX.Element {
  const set = (facing: number | null, key: string | null = null): void => onChange((entry) => (entry.type === 'player' ? withFacing(entry, facing) : entry), key);
  return (
    <div className="space-y-2 sm:col-span-2">
      <p className="text-sm font-medium text-slate-700">{FACING_LABEL}</p>
      <div role="group" aria-label="Blickrichtung" className="grid grid-cols-5 gap-1.5 sm:grid-cols-9">
        {FACING_DIRECTIONS.map((direction) => (
          <Button
            key={direction.angle}
            type="button"
            variant="secondary"
            size="sm"
            aria-label={`Blick nach ${direction.label}`}
            aria-pressed={element.facing === direction.angle}
            disabled={disabled}
            className={cn('px-1 text-base', element.facing === direction.angle && 'ring-2 ring-brand-500')}
            onClick={() => set(direction.angle)}
          >
            {direction.glyph}
          </Button>
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label="Keine Blickrichtung"
          aria-pressed={element.facing === undefined}
          disabled={disabled}
          className={cn('col-span-2 px-1 sm:col-span-1', element.facing === undefined && 'ring-2 ring-brand-500')}
          onClick={() => set(null)}
        >
          Keine
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={0}
          max={359}
          step={1}
          value={element.facing ?? 0}
          disabled={disabled}
          aria-label="Blickrichtung in Grad"
          onChange={(event) => set(Number(event.target.value), `facing-${element.x}-${element.y}`)}
          className="min-h-11 flex-1"
        />
        <span className="w-12 text-right text-sm tabular-nums text-slate-700">{element.facing === undefined ? '–' : `${element.facing}°`}</span>
      </div>
      <p className="text-xs text-slate-500">Im Feld lässt sich der Griff am Spieler drehen (rastet mit „Einrasten“ auf 45°). Im Ablauf schaut er beim Laufen in Laufrichtung.</p>
    </div>
  );
}

/** Eigenschaften einer Masslinie: Beschriftung mit Vorschlaegen und der Vorschlag "Massstab" aus der Feldgroesse. */
function MeasureProperties({
  element,
  index,
  pitch,
  areaWidth,
  onAreaWidth,
  disabled,
  onChange,
}: {
  element: Extract<DiagramElement, { type: 'measure' }>;
  index: number | null;
  pitch: DiagramPitch;
  areaWidth: string;
  onAreaWidth: (value: string) => void;
  disabled?: boolean;
  onChange: (change: (element: DiagramElement) => DiagramElement, key?: string | null) => void;
}): JSX.Element {
  const setLabel = (text: string, key: string | null): void =>
    onChange((entry) => {
      if (entry.type !== 'measure') return entry;
      const { label: _alt, ...rest } = entry;
      const label = text.replace(/^\s+/, '').slice(0, DIAGRAM_MEASURE_LABEL_MAX);
      return label.trim() ? { ...rest, label } : rest;
    }, key);

  // Ganze und halbe Felder messen wie das Grossfeld; bei der freien Flaeche gibt man die Breite an.
  const width = pitch === 'AREA' ? (/^\d{1,3}$/.test(areaWidth.trim()) ? Number(areaWidth.trim()) : null) : PITCH_WIDTH_METERS;
  const suggestion =
    width !== null && width > 0
      ? formatMeters(measureMeters({ x: element.from[0], y: element.from[1] }, { x: element.to[0], y: element.to[1] }, pitch, width))
      : null;

  return (
    <div className="space-y-3">
      <Field label="Beschriftung" hint={`Höchstens ${DIAGRAM_MEASURE_LABEL_MAX} Zeichen, z. B. „15 m“. Leer = nur die Linie. Die Enden lassen sich an den weißen Punkten ziehen.`}>
        {(id) => (
          <Input
            id={id}
            value={element.label ?? ''}
            maxLength={DIAGRAM_MEASURE_LABEL_MAX}
            autoComplete="off"
            disabled={disabled}
            // Enter darf das umgebende Formular nicht abschicken.
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault();
            }}
            onChange={(event) => setLabel(event.target.value, `measure-label-${index}`)}
          />
        )}
      </Field>
      <div role="group" aria-label="Vorschläge für die Beschriftung" className="flex flex-wrap gap-1.5">
        {MEASURE_SUGGESTIONS.map((text) => (
          <Button key={text} type="button" variant="secondary" size="sm" disabled={disabled} aria-pressed={element.label === text} onClick={() => setLabel(text, null)}>
            {text}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        {pitch === 'AREA' && (
          <div className="w-40">
            <Field label="Breite der Fläche (m)">
              {(id) => (
                <Input
                  id={id}
                  value={areaWidth}
                  inputMode="numeric"
                  maxLength={3}
                  autoComplete="off"
                  disabled={disabled}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                  onChange={(event) => onAreaWidth(event.target.value.replace(/\D/g, '').slice(0, 3))}
                />
              )}
            </Field>
          </div>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled || suggestion === null}
          aria-label="Maßstab übernehmen"
          title={pitch === 'AREA' ? 'Rechnet mit der Breite der Fläche' : `Rechnet mit der Feldbreite von ${PITCH_WIDTH_METERS} m`}
          onClick={() => suggestion && setLabel(suggestion, null)}
        >
          Maßstab{suggestion ? `: ${suggestion}` : ''}
        </Button>
      </div>
    </div>
  );
}
